// @ts-nocheck
"use client";

// src/pages/users/WorkerDetails.jsx
import { useEffect, useMemo, useState } from "react";
import {
  Badge,
  Card,
  CardBody,
  CardHeader,
  Col,
  Row,
  Table,
  Form,
  Label,
  Input,
  FormFeedback,
  Button,
  Collapse,
} from "reactstrap";
import Breadcrumbs from "../../../components/Common/Breadcrumb";
import ConfirmModal from "../../../components/Common/ConfirmModal";
import withRouter from "../../../components/Common/withRouter";
import {
  useCreateUser,
  useUserDetails,
  useUpdateWorkerByAdmin,
} from "../../../api/users";
import { useFormik } from "formik";
import * as Yup from "yup";
import toastr from "toastr";
import React from "react";
import { mediaUrl } from "../../../helpers/mediaHelper";
import {
  getStarterChecklistOption,
  labelStudentLoanPlanType,
  RIGHT_TO_WORK_STATUS_BRITISH_IRISH,
  FILE_VALIDATION,
  TRUSTID_ADMIN_PORTAL_URL,
  UK_CHECK_IMMIGRATION_STATUS_URL,
  UK_DVLA_DRIVING_RECORD_VALIDATE_URL,
  UK_DBS_UPDATE_SERVICE_URL,
  CSCS_SMART_CHECK_URL,
  WORKER_EXPERIENCE_LEVEL_OPTIONS,
} from "../../../constants/workerOnboarding";
import { ProofOfAddressAcceptableContent } from "../../../components/Common/ProofOfAddressAcceptableHint";
import { useTranslation } from "react-i18next";
import {
  DBS_TYPE_OPTIONS,
  WORKFORCE_OPTIONS,
  dbsTypeRequiresWorkforce,
  mapDbsItemFromApi,
  buildDbsCompliancePayload,
  buildOtherCertsCompliancePayload,
  isCvCertificate,
} from "../../../helpers/dbsCertHelper";
import {
  normalizeUserStatus,
  userStatusLabel,
} from "../../../helpers/userStatusHelper";

const adminDbsRowSchema = Yup.object({
  dbsSlug: Yup.string().required("DBS slug is required"),
  status: Yup.boolean().required("Required"),
  issueDate: Yup.string().notRequired(),
  certificateRef: Yup.string().notRequired(),
  lastCheckedDate: Yup.string().notRequired(),
  expiryDate: Yup.string().when("status", {
    is: true,
    then: (schema) => schema.required("Expiry date is required"),
    otherwise: (schema) => schema.notRequired(),
  }),
  dbsType: Yup.string().when("status", {
    is: true,
    then: (schema) =>
      schema.required("DBS type is required when approving DBS"),
    otherwise: (schema) => schema.notRequired(),
  }),
  workForceUi: Yup.string().when(["status", "dbsType"], {
    is: (status, dbsType) =>
      status === true && dbsTypeRequiresWorkforce(dbsType),
    then: (schema) =>
      schema.required(
        "Workforce is required for Enhanced or Enhanced Plus DBS",
      ),
    otherwise: (schema) => schema.notRequired(),
  }),
});

const patchAdminDbsRow = (row, partial) => {
  let next = { ...row, ...partial };
  if (partial.status === false) {
    next = { ...next, dbsType: "", workForceUi: "", expiryDate: "" };
  }
  if (
    Object.prototype.hasOwnProperty.call(partial, "dbsType") &&
    !dbsTypeRequiresWorkforce(partial.dbsType)
  ) {
    next = { ...next, workForceUi: "" };
  }
  return next;
};

const adminOtherCertRowSchema = Yup.object({
  certSlug: Yup.string().required("Cert slug is required"),
  status: Yup.boolean().required("Required"),
  expiryDate: Yup.string().when(["status", "certSlug"], {
    is: (status, certSlug) => status === true && !isCvCertificate(certSlug),
    then: (schema) => schema.required("Expiry date is required"),
    otherwise: (schema) => schema.notRequired(),
  }),
});

const patchAdminOtherCertRow = (row, partial) => {
  let next = { ...row, ...partial };
  if (partial.status === false) {
    next = { ...next, expiryDate: "" };
  }
  return next;
};

function getOtherCertFiles(certItem) {
  if (Array.isArray(certItem?.Files) && certItem.Files.length > 0) {
    return certItem.Files;
  }
  if (Array.isArray(certItem?.File) && certItem.File.length > 0) {
    return certItem.File;
  }
  return [];
}

function hasUploadedMediaFile(value) {
  if (Array.isArray(value) && value.length > 0) return true;
  return typeof value === "string" && value.trim() !== "";
}
//comment
function hasAdminRtwHomeOfficeFileEvidence(rightToWork = {}) {
  return (
    rightToWork.homeOfficeFile instanceof File ||
    hasUploadedMediaFile(rightToWork.homeOfficeFileUrl)
  );
}

function hasAdminRtwTrustIdEvidence(rightToWork = {}) {
  return (
    rightToWork.trustIdFile instanceof File ||
    hasUploadedMediaFile(rightToWork.trustIdFileUrl)
  );
}

function hasAdminRtwEvidence(rightToWork = {}) {
  return (
    hasAdminRtwHomeOfficeFileEvidence(rightToWork) ||
    hasAdminRtwTrustIdEvidence(rightToWork)
  );
}

function resolveAdminRtwErrors(rtwErrors) {
  if (typeof rtwErrors === "string") {
    return {
      message: rtwErrors,
      evidenceMessage: rtwErrors,
      fieldErrors: {},
    };
  }
  if (!rtwErrors || typeof rtwErrors !== "object") {
    return { message: "", evidenceMessage: "", fieldErrors: {} };
  }

  const fieldErrors = { ...rtwErrors };
  const objectTestMessage = fieldErrors["rtw-evidence"];
  delete fieldErrors["rtw-evidence"];

  const message =
    objectTestMessage ||
    fieldErrors.homeOfficeFile ||
    fieldErrors.trustIdFile ||
    "";

  return {
    message: String(message || ""),
    evidenceMessage: String(objectTestMessage || ""),
    fieldErrors,
  };
}

function shouldShowAdminRtwErrors({ submitCount, touched }) {
  return submitCount > 0 || !!touched?.homeOfficeFile || !!touched?.trustIdFile;
}

function isUnderFileSizeLimit(file, maxMb) {
  return file.size <= maxMb * FILE_VALIDATION.BYTES_IN_MB;
}

function isAdminRtwPdfFile(file) {
  if (!(file instanceof File)) return false;
  return (
    FILE_VALIDATION.ALLOWED_PDF_EXTENSION.test(file.name) ||
    file.type === "application/pdf"
  );
}

function handleAdminRtwPdfFileChange(setRtwField, field, event) {
  const input = event.currentTarget;
  const file = input.files?.[0] || null;

  if (!file) {
    setRtwField(field, null);
    return;
  }

  if (!isAdminRtwPdfFile(file)) {
    toastr.error("File must be a PDF");
    input.value = "";
    setRtwField(field, null);
    return;
  }

  if (!isUnderFileSizeLimit(file, FILE_VALIDATION.MAX_MB)) {
    toastr.error(`File must be under ${FILE_VALIDATION.MAX_MB}MB`);
    input.value = "";
    setRtwField(field, null);
    return;
  }

  setRtwField(field, file);
}

function appendAdminWorkerFile(fd, key, file) {
  if (file instanceof File) {
    fd.append(key, file, file.name || undefined);
  }
}

function buildAdminWorkerMutationRequest({
  slug,
  email,
  role,
  status,
  tier,
  reason,
  worker,
  homeOfficeFile,
  trustIdFile,
}: {
  slug?: string;
  email?: string;
  role?: string;
  status: string;
  tier: string;
  reason?: string;
  worker: Record<string, unknown>;
  homeOfficeFile?: File | null;
  trustIdFile?: File | null;
}) {
  const trimmedReason =
    typeof reason === "string" && reason.trim() ? reason.trim() : undefined;
  const includeReason =
    normalizeUserStatus(status) === "actionRequired" && !!trimmedReason;

  const hasFiles =
    homeOfficeFile instanceof File || trustIdFile instanceof File;
  if (!hasFiles) {
    return {
      payload: {
        ...(slug ? { slug } : {}),
        ...(email ? { email } : {}),
        ...(role ? { role } : {}),
        status,
        tier,
        ...(includeReason ? { reason: trimmedReason } : {}),
        worker,
      },
    };
  }

  const fd = new FormData();
  if (email) fd.append("email", email);
  if (role) fd.append("role", role);
  fd.append("status", status);
  fd.append("tier", tier);
  if (includeReason) fd.append("reason", trimmedReason);
  fd.append("worker", JSON.stringify(worker));
  appendAdminWorkerFile(fd, "homeOfficeFile", homeOfficeFile);
  appendAdminWorkerFile(fd, "trustIdFile", trustIdFile);

  return slug ? { slug, data: fd } : { data: fd };
}

function AdminRtwFileField({
  label,
  fileInputId,
  file,
  fileUrl,
  onFileChange,
  labelClassName = "form-label",
  accept = ".pdf,application/pdf",
}) {
  const storedUrl = Array.isArray(fileUrl) ? fileUrl[0] : fileUrl;
  const resolvedUrl = mediaUrl(storedUrl);

  return (
    <>
      <Label className={labelClassName} htmlFor={fileInputId}>
        {label}
      </Label>
      {file instanceof File ? (
        <p className="small text-muted mb-2">{file.name}</p>
      ) : hasUploadedMediaFile(storedUrl) && resolvedUrl ? (
        <div className="mb-2">
          <a href={resolvedUrl} target="_blank" rel="noreferrer">
            View current file
          </a>
        </div>
      ) : null}
      <Input
        id={fileInputId}
        type="file"
        accept={accept}
        onChange={onFileChange}
      />
    </>
  );
}

function StarterChecklistChoiceDisplay({ value }) {
  const option = getStarterChecklistOption(value);
  if (!option) {
    return <span>{value || "-"}</span>;
  }

  return (
    <div className="worker-admin-starter-checklist-choice">
      <p className="mb-0">{option.intro}</p>
      {option.bullets?.length > 0 && (
        <ul className="mb-0 mt-1 ps-3">
          {option.bullets.map((item) => (
            <li key={item}>{item}</li>
          ))}
        </ul>
      )}
    </div>
  );
}

function isCscsCertificate(certSlug, certTitle = "") {
  const haystack = `${certSlug ?? ""} ${certTitle ?? ""}`.toLowerCase();
  return haystack.includes("cscs");
}

function AdminExternalLinks({ links }) {
  if (!Array.isArray(links) || links.length === 0) return null;

  return (
    <ul className="worker-admin-rtw-group__links">
      {links.map(({ href, label }) => (
        <li key={href}>
          <a href={href} target="_blank" rel="noopener noreferrer">
            {label}
          </a>
        </li>
      ))}
    </ul>
  );
}

function normalizeRtwVerificationStatus(value) {
  if (value === true || String(value).toLowerCase() === "verified") return true;
  if (value === false || String(value).toLowerCase() === "unverified")
    return false;
  return null;
}

function formatRtwVerificationStatus(value) {
  const normalized = normalizeRtwVerificationStatus(value);
  if (normalized === true) return "Verified";
  if (normalized === false) return "Unverified";
  return null;
}

function AdminVerificationStatusReadonly({ label, status }) {
  const icon =
    status === true
      ? "mdi-check-circle-outline"
      : status === false
        ? "mdi-close-circle-outline"
        : "mdi-clock-outline";
  const text =
    status === true
      ? "Verified"
      : status === false
        ? "Unverified"
        : "Pending review";
  const variantClass =
    status === true
      ? "border-success text-success bg-success bg-opacity-10"
      : status === false
        ? "border-danger text-danger bg-danger bg-opacity-10"
        : "border-secondary text-secondary bg-light";

  return (
    <>
      <Label className="form-label text-muted">{label}</Label>
      <div>
        <span
          className={`badge rounded-pill border fw-semibold px-3 py-2 d-inline-flex align-items-center gap-1 ${variantClass}`}
        >
          <i className={`mdi ${icon}`} aria-hidden />
          {text}
        </span>
      </div>
    </>
  );
}

function AdminDbsCertFields({
  dbsItem,
  label,
  onChange,
  rowErrors = {},
  showErrors = false,
}) {
  const showApprovedFields = !!dbsItem.status;
  const showWorkforce = dbsTypeRequiresWorkforce(dbsItem.dbsType);
  const dbsTypeError = showErrors ? rowErrors.dbsType : "";
  const workForceError = showErrors ? rowErrors.workForceUi : "";
  const expiryDateError = showErrors ? rowErrors.expiryDate : "";

  return (
    <Card className="mb-3 border">
      <CardBody>
        <Row className="gy-2">
          <Col md={12}>
            <small className="text-muted fw-bolder">{label}</small>
            <AdminExternalLinks
              links={[
                {
                  href: UK_DBS_UPDATE_SERVICE_URL,
                  label: "DBS Update Service checker",
                },
              ]}
            />
          </Col>
          <Col md={3}>
            <Label className="form-label">Status</Label>
            <Input
              type="select"
              value={dbsItem.status ? "true" : "false"}
              onChange={(e) =>
                onChange(
                  patchAdminDbsRow(dbsItem, {
                    status: e.target.value === "true",
                  }),
                )
              }
            >
              <option value="false">Unverified</option>
              <option value="true">Verified</option>
            </Input>
          </Col>
          {showApprovedFields && (
            <>
              <Col md={3}>
                <Label className="form-label">DBS type *</Label>
                <Input
                  type="select"
                  value={dbsItem.dbsType || ""}
                  onChange={(e) =>
                    onChange(
                      patchAdminDbsRow(dbsItem, { dbsType: e.target.value }),
                    )
                  }
                  invalid={!!dbsTypeError}
                >
                  <option value="">Select DBS type</option>
                  {DBS_TYPE_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </Input>
                {dbsTypeError ? (
                  <FormFeedback className="d-block">
                    {dbsTypeError}
                  </FormFeedback>
                ) : null}
              </Col>
              {showWorkforce && (
                <Col md={3}>
                  <Label className="form-label">Workforce *</Label>
                  <Input
                    type="select"
                    value={dbsItem.workForceUi || ""}
                    onChange={(e) =>
                      onChange(
                        patchAdminDbsRow(dbsItem, {
                          workForceUi: e.target.value,
                        }),
                      )
                    }
                    invalid={!!workForceError}
                  >
                    <option value="">Select workforce</option>
                    {WORKFORCE_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </Input>
                  {workForceError ? (
                    <FormFeedback className="d-block">
                      {workForceError}
                    </FormFeedback>
                  ) : null}
                </Col>
              )}
              <Col md={3}>
                <Label className="form-label">Issue date (optional)</Label>
                <Input
                  type="date"
                  value={dbsItem.issueDate || ""}
                  onChange={(e) =>
                    onChange(
                      patchAdminDbsRow(dbsItem, { issueDate: e.target.value }),
                    )
                  }
                />
              </Col>
              <Col md={3}>
                <Label className="form-label">Certificate ref (optional)</Label>
                <Input
                  type="text"
                  value={dbsItem.certificateRef || ""}
                  onChange={(e) =>
                    onChange(
                      patchAdminDbsRow(dbsItem, {
                        certificateRef: e.target.value,
                      }),
                    )
                  }
                />
              </Col>
              <Col md={3}>
                <Label className="form-label">Last Checked Date (optional)</Label>
                <Input
                  type="date"
                  value={dbsItem.lastCheckedDate || ""}
                  onChange={(e) =>
                    onChange(
                      patchAdminDbsRow(dbsItem, {
                        lastCheckedDate: e.target.value,
                      }),
                    )
                  }
                />
              </Col>
              <Col md={3}>
                <Label className="form-label">Expiry date *</Label>
                <Input
                  type="date"
                  value={dbsItem.expiryDate || ""}
                  onChange={(e) =>
                    onChange(
                      patchAdminDbsRow(dbsItem, { expiryDate: e.target.value }),
                    )
                  }
                  invalid={!!expiryDateError}
                />
                {expiryDateError ? (
                  <FormFeedback className="d-block">
                    {expiryDateError}
                  </FormFeedback>
                ) : null}
              </Col>
            </>
          )}
        </Row>
      </CardBody>
    </Card>
  );
}

function AdminDrivingComplianceFormFields({
  rightToWork,
  setRtwField,
  hasDrivingLicenseEvidence,
  hasDvlaShareCodeValue,
  labelClassName = "form-label",
  dvlaCodeId = "rtw_dvla_code",
  errors = {},
  touched = {},
  submitCount = 0,
}) {
  const dlVerified = rightToWork.drivingLicenseStatus === true;
  const showErrors =
    submitCount > 0 ||
    !!touched?.drivingLicenseExpiryDate ||
    !!touched?.visaExpiryDate;
  const drivingLicenseExpiryDateError = showErrors
    ? errors.drivingLicenseExpiryDate
    : "";

  const setTriStateBool = (field, rawValue) => {
    const verified = rawValue === "true";
    setRtwField(field, rawValue === "" ? null : verified);
    return verified;
  };

  const rtwGroup = (label, children) => (
    <Col md={12}>
      <div className="worker-admin-rtw-group">
        <p className="worker-admin-rtw-group__label">{label}</p>
        <Row className="gy-3">{children}</Row>
      </div>
    </Col>
  );

  return (
    <>
      {hasDvlaShareCodeValue &&
        rtwGroup(
          "DVLA share code",
          <>
            <Col md={4}>
              <Label className={labelClassName} htmlFor={dvlaCodeId}>
                DVLA share code (read-only)
              </Label>
              <Input
                id={dvlaCodeId}
                type="text"
                value={rightToWork.dvlaShareCode}
                readOnly
                disabled
              />
              <AdminExternalLinks
                links={[
                  {
                    href: UK_DVLA_DRIVING_RECORD_VALIDATE_URL,
                    label: "Check driving status",
                  },
                ]}
              />
            </Col>
          </>,
        )}

      {hasDrivingLicenseEvidence &&
        rtwGroup(
          "Driving licence",
          <>
            <Col md={4}>
              <Label className={labelClassName}>Driving licence status</Label>
              <Input
                type="select"
                value={
                  rightToWork.drivingLicenseStatus === true
                    ? "true"
                    : rightToWork.drivingLicenseStatus === false
                      ? "false"
                      : ""
                }
                onChange={(e) => {
                  const verified = setTriStateBool(
                    "drivingLicenseStatus",
                    e.target.value,
                  );
                  if (!verified) {
                    setRtwField("drivingLicenseExpiryDate", "");
                  }
                }}
              >
                <option value="">Not set</option>
                <option value="false">Unverified</option>
                <option value="true">Verified</option>
              </Input>
            </Col>
            {dlVerified && (
              <Col md={4}>
                <Label className={labelClassName}>Driving licence expiry</Label>
                <Input
                  type="date"
                  value={rightToWork.drivingLicenseExpiryDate || ""}
                  onChange={(e) =>
                    setRtwField("drivingLicenseExpiryDate", e.target.value)
                  }
                  invalid={!!drivingLicenseExpiryDateError}
                />
                {drivingLicenseExpiryDateError ? (
                  <FormFeedback className="d-block">
                    {drivingLicenseExpiryDateError}
                  </FormFeedback>
                ) : null}
              </Col>
            )}
          </>,
        )}
    </>
  );
}

function AdminRightToWorkFormFields({
  mode,
  rightToWork,
  setRtwField,
  showVisaExpiryAdminField,
  labelClassName = "form-label",
  homeOfficeFileId = "rtw_home_office_file",
  trustIdFileId = "rtw_trust_id_file",
  errors = {},
  touched = {},
  submitCount = 0,
  requireRtwEvidence = true,
}) {
  const isCreate = mode === "create";
  const { evidenceMessage, fieldErrors } = resolveAdminRtwErrors(errors);
  const showRtwErrors = shouldShowAdminRtwErrors({ submitCount, touched });
  const homeOfficeFileError = showRtwErrors
    ? fieldErrors.homeOfficeFile || ""
    : "";
  const trustIdFileError = showRtwErrors ? fieldErrors.trustIdFile || "" : "";
  const visaExpiryDateError = showRtwErrors
    ? fieldErrors.visaExpiryDate || ""
    : "";
  const rtwEvidenceError =
    requireRtwEvidence &&
    showRtwErrors &&
    !homeOfficeFileError &&
    !trustIdFileError
      ? evidenceMessage
      : "";

  const rtwFileHint = requireRtwEvidence
    ? `PDF only (max ${FILE_VALIDATION.MAX_MB}MB). Provide a Home Office file and/or a TrustID file (at least one is required).`
    : `PDF only (max ${FILE_VALIDATION.MAX_MB}MB). Optional when declining — upload only if you have the documents.`;

  const rtwGroup = (label, children) => (
    <Col md={12}>
      <div className="worker-admin-rtw-group">
        <p className="worker-admin-rtw-group__label">{label}</p>
        <Row className="gy-3">{children}</Row>
      </div>
    </Col>
  );

  return (
    <>
      {rtwGroup(
        "Home Office",
        <>
          <Col md={12}>
            <AdminRtwFileField
              label={
                requireRtwEvidence
                  ? "Home Office File (PDF)"
                  : "Home Office File (PDF, optional)"
              }
              fileInputId={homeOfficeFileId}
              file={rightToWork.homeOfficeFile}
              fileUrl={rightToWork.homeOfficeFileUrl}
              labelClassName={labelClassName}
              onFileChange={(e) =>
                handleAdminRtwPdfFileChange(setRtwField, "homeOfficeFile", e)
              }
            />
            {homeOfficeFileError ? (
              <FormFeedback className="d-block">
                {homeOfficeFileError}
              </FormFeedback>
            ) : rtwEvidenceError ? (
              <FormFeedback className="d-block">
                {rtwEvidenceError}
              </FormFeedback>
            ) : (
              <small className="text-muted d-block mt-1">{rtwFileHint}</small>
            )}
            <AdminExternalLinks
              links={[
                {
                  href: UK_CHECK_IMMIGRATION_STATUS_URL,
                  label: "Check immigration status (GOV.UK)",
                },
              ]}
            />
          </Col>
        </>,
      )}

      {rtwGroup(
        "TrustID",
        <>
          <Col md={isCreate ? 12 : 6}>
            <AdminRtwFileField
              label={
                requireRtwEvidence
                  ? "TrustID file (admin, PDF)"
                  : "TrustID file (admin, PDF, optional)"
              }
              fileInputId={trustIdFileId}
              file={rightToWork.trustIdFile}
              fileUrl={rightToWork.trustIdFileUrl}
              labelClassName={labelClassName}
              onFileChange={(e) =>
                handleAdminRtwPdfFileChange(setRtwField, "trustIdFile", e)
              }
            />
            <AdminExternalLinks
              links={[
                {
                  href: TRUSTID_ADMIN_PORTAL_URL,
                  label: "TrustID admin portal",
                },
              ]}
            />
            {trustIdFileError ? (
              <FormFeedback className="d-block">
                {trustIdFileError}
              </FormFeedback>
            ) : rtwEvidenceError ? (
              <FormFeedback className="d-block">
                {rtwEvidenceError}
              </FormFeedback>
            ) : (
              <small className="text-muted d-block mt-1">
                {requireRtwEvidence
                  ? `PDF only (max ${FILE_VALIDATION.MAX_MB}MB).`
                  : `PDF only (max ${FILE_VALIDATION.MAX_MB}MB). Optional when declining.`}
              </small>
            )}
          </Col>
        </>,
      )}

      {showVisaExpiryAdminField &&
        rtwGroup(
          "Visa",
          <Col md={4}>
            <Label className={labelClassName}>Visa expiry *</Label>
            <Input
              type="date"
              value={rightToWork.visaExpiryDate || ""}
              onChange={(e) => setRtwField("visaExpiryDate", e.target.value)}
              invalid={!!visaExpiryDateError}
            />
            {visaExpiryDateError ? (
              <FormFeedback className="d-block">
                {visaExpiryDateError}
              </FormFeedback>
            ) : null}
          </Col>,
        )}

      <Col md={4}>
        <Label className={labelClassName}>Status</Label>
        <Input
          type="select"
          value={
            rightToWork.rtwStatus === true
              ? "true"
              : rightToWork.rtwStatus === false
                ? "false"
                : ""
          }
          onChange={(e) => {
            const rawValue = e.target.value;
            setRtwField(
              "rtwStatus",
              rawValue === "" ? null : rawValue === "true",
            );
          }}
        >
          <option value="">Not set</option>
          <option value="false">Unverified</option>
          <option value="true">Verified</option>
        </Input>
      </Col>
    </>
  );
}

function WorkerAdminInlineBlock({ title = "Admin review", children }) {
  return (
    <div className="worker-admin-inline-block mt-4 pt-3 border-top">
      <h6 className="worker-admin-inline-block__title text-muted text-uppercase small mb-3">
        {title}
      </h6>
      {children}
    </div>
  );
}

const WORKER_DETAILS_SECTION_HEADER_STYLE = {
  cursor: "pointer",
  backgroundColor: "#ededed",
};

const WORKER_DETAILS_SECTION_CHEVRON_STYLE = {
  width: "20px",
  height: "20px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontSize: "12px",
};

function WorkerDetailsCollapsibleCard({
  title,
  isOpen,
  onToggle,
  children,
  className = "mb-3",
}) {
  return (
    <Card className={className}>
      <CardHeader
        onClick={() => onToggle((open) => !open)}
        className="d-flex justify-content-between align-items-center"
        style={WORKER_DETAILS_SECTION_HEADER_STYLE}
      >
        <h5 className="mb-0">{title}</h5>
        <i
          className={`mdi mdi-chevron-${
            isOpen ? "up" : "down"
          } bg-dark text-white rounded-circle`}
          style={WORKER_DETAILS_SECTION_CHEVRON_STYLE}
        />
      </CardHeader>
      <Collapse isOpen={isOpen}>
        <CardBody>{children}</CardBody>
      </Collapse>
    </Card>
  );
}

function WorkerAdminFormSection({ title, description, icon, children }) {
  return (
    <div className="worker-admin-form-section">
      <div className="worker-admin-form-section__head">
        {icon ? (
          <span className="worker-admin-form-section__icon" aria-hidden>
            <i className={`mdi ${icon}`} />
          </span>
        ) : null}
        <div>
          <h6 className="worker-admin-form-section__title">{title}</h6>
          {description ? (
            <p className="text-muted small mb-0 mt-1">{description}</p>
          ) : null}
        </div>
      </div>
      <Row className="gy-3">{children}</Row>
    </div>
  );
}

function WorkerAdminFormFooter({ hint, children }) {
  return (
    <div className="worker-admin-form-footer">
      {hint ? (
        <p className="worker-admin-form-footer__hint">{hint}</p>
      ) : (
        <span />
      )}
      <div className="d-flex flex-column align-items-end">{children}</div>
    </div>
  );
}

function AdminOtherCertsEditor({
  certs,
  onChange,
  labelClassName = "form-label",
  rowErrors = [],
  showErrors = false,
}) {
  if (!Array.isArray(certs) || certs.length === 0) return null;

  const patchCert = (idx, partial) => {
    const newCerts = [...certs];
    newCerts[idx] = patchAdminOtherCertRow(newCerts[idx], partial);
    onChange(newCerts);
  };

  return (
    <Row className="gy-3">
      {certs.map((certItem, idx) => {
        const isVerified = certItem.status === true;
        const hideExpiry = isCvCertificate(certItem.certSlug);
        const expiryDateError =
          showErrors &&
          typeof rowErrors[idx] === "object" &&
          rowErrors[idx] !== null
            ? rowErrors[idx].expiryDate
            : "";

        return (
          <Col key={certItem.certSlug || idx} md={6} lg={4}>
            <Card className="worker-admin-cert-card h-100">
              <CardBody>
                <div className="worker-admin-cert-card__title">
                  {certItem.certSlug || `Certificate ${idx + 1}`}
                </div>
                {isCscsCertificate(certItem.certSlug) && (
                  <AdminExternalLinks
                    links={[
                      {
                        href: CSCS_SMART_CHECK_URL,
                        label: "CSCS Smart Check",
                      },
                    ]}
                  />
                )}
                <Row className="gy-2">
                  <Col md={12}>
                    <Label className={labelClassName}>Status</Label>
                    <Input
                      type="select"
                      value={certItem.status ? "true" : "false"}
                      onChange={(e) =>
                        patchCert(idx, {
                          status: e.target.value === "true",
                        })
                      }
                    >
                      <option value="false">Unverified</option>
                      <option value="true">Verified</option>
                    </Input>
                  </Col>
                  {isVerified && !hideExpiry && (
                    <Col md={12}>
                      <Label className={labelClassName}>Expiry date *</Label>
                      <Input
                        type="date"
                        value={certItem.expiryDate || ""}
                        onChange={(e) =>
                          patchCert(idx, { expiryDate: e.target.value })
                        }
                        invalid={!!expiryDateError}
                      />
                      {expiryDateError ? (
                        <FormFeedback className="d-block">
                          {expiryDateError}
                        </FormFeedback>
                      ) : null}
                    </Col>
                  )}
                </Row>
              </CardBody>
            </Card>
          </Col>
        );
      })}
    </Row>
  );
}

function buildWorkerPersonalInformationPayload({ experienceLevel }) {
  return {
    experienceLevel: experienceLevel ? String(experienceLevel) : null,
  };
}

function AdminWorkerAccountFields({
  variant,
  formik,
  labelClassName,
  statusOptions,
  roleOptions,
  tierOptions,
}) {
  const isCreate = variant === "create";
  const values = formik.values;
  const handleChange = formik.handleChange;
  const handleBlur = formik.handleBlur;
  const touched = formik.touched;
  const errors = formik.errors;
  const showActionRequiredReason =
    normalizeUserStatus(values.status) === "actionRequired";

  const handleStatusChange = (e) => {
    handleChange(e);
    if (normalizeUserStatus(e.target.value) !== "actionRequired") {
      formik.setFieldValue("reason", "");
    }
  };

  return (
    <WorkerAdminFormSection
      title={isCreate ? "Account & access" : "Account settings"}
      icon="mdi-shield-account-outline"
      description={
        isCreate
          ? "Review worker identity and set approval status before creating the account."
          : "Update approval status, tier, and platform access for this worker."
      }
    >
      {isCreate && (
        <Col md={12}>
          <Row className="gy-3">
            <Col md={6}>
              <Label className={labelClassName} htmlFor="email">
                Email
              </Label>
              <Input
                id="email"
                name="email"
                type="email"
                value={values.email}
                onChange={handleChange}
                onBlur={handleBlur}
                disabled
                readOnly
                invalid={touched.email && !!errors.email}
              />
            </Col>
            <Col md={6}>
              <Label className={labelClassName} htmlFor="role">
                Role
              </Label>
              <Input
                id="role"
                name="role"
                type="select"
                value={values.role}
                disabled
                onChange={handleChange}
                onBlur={handleBlur}
                invalid={touched.role && !!errors.role}
              >
                {roleOptions.map((r) => (
                  <option key={r} value={r}>
                    {r}
                  </option>
                ))}
              </Input>
            </Col>
          </Row>
        </Col>
      )}
      <Col md={12}>
        <Row className="gy-3">
          <Col md={4}>
            <Label
              className={labelClassName}
              htmlFor={isCreate ? "status_dropdown" : "status_u"}
            >
              Status
            </Label>
            <Input
              id={isCreate ? "status_dropdown" : "status_u"}
              name="status"
              type="select"
              value={values.status}
              onChange={handleStatusChange}
              onBlur={handleBlur}
              invalid={touched.status && !!errors.status}
            >
              {statusOptions.map((t) => (
                <option key={t} value={t}>
                  {userStatusLabel(t)}
                </option>
              ))}
            </Input>
            <FormFeedback>{errors.status}</FormFeedback>
          </Col>
          <Col md={4}>
            <Label
              className={labelClassName}
              htmlFor={isCreate ? "tier" : "tier_u"}
            >
              Tier
            </Label>
            <Input
              id={isCreate ? "tier" : "tier_u"}
              name="tier"
              type="select"
              value={values.tier}
              onChange={handleChange}
              onBlur={handleBlur}
              invalid={touched.tier && !!errors.tier}
            >
              {tierOptions.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </Input>
            <FormFeedback>{errors.tier}</FormFeedback>
          </Col>
          {showActionRequiredReason && (
            <Col md={4}>
              <Label
                className={labelClassName}
                htmlFor={isCreate ? "reason" : "reason_u"}
              >
                Reason
              </Label>
              <Input
                id={isCreate ? "reason" : "reason_u"}
                name="reason"
                type="textarea"
                rows={3}
                value={values.reason || ""}
                onChange={handleChange}
                onBlur={handleBlur}
                invalid={touched.reason && !!errors.reason}
                placeholder="Why action is required"
              />
              <FormFeedback>{errors.reason}</FormFeedback>
            </Col>
          )}
        </Row>
      </Col>
    </WorkerAdminFormSection>
  );
}

function AdminWorkerProfileFields({ variant, formik, labelClassName }) {
  const isCreate = variant === "create";
  const values = formik.values;
  const handleChange = formik.handleChange;
  const handleBlur = formik.handleBlur;
  const touched = formik.touched;
  const errors = formik.errors;

  return (
    <WorkerAdminFormSection
      title="Profile"
      icon="mdi-account-edit-outline"
      description="Experience level used when presenting this worker to clients."
    >
      <Col md={6}>
        <Label
          className={labelClassName}
          htmlFor={isCreate ? "experienceLevel" : "experienceLevel_u"}
        >
          Experience level
        </Label>
        <Input
          id={isCreate ? "experienceLevel" : "experienceLevel_u"}
          name="experienceLevel"
          type="select"
          value={values.experienceLevel}
          onChange={handleChange}
          onBlur={handleBlur}
          invalid={touched.experienceLevel && !!errors.experienceLevel}
        >
          <option value="">Not set</option>
          {WORKER_EXPERIENCE_LEVEL_OPTIONS.map((level) => (
            <option key={level} value={level}>
              {level}
            </option>
          ))}
        </Input>
        <FormFeedback>{errors.experienceLevel}</FormFeedback>
      </Col>
    </WorkerAdminFormSection>
  );
}

function AdminWorkerRightToWorkFields({
  variant,
  formik,
  labelClassName,
  homeOfficeFileId,
  trustIdFileId = "rtw_trust_id_file",
  showVisaExpiryAdminField,
}) {
  const values = formik.values;
  const touched = formik.touched?.rightToWork || {};
  const submitCount = formik.submitCount || 0;
  const setFieldValue = formik.setFieldValue.bind(formik);
  const requireRtwEvidence =
    normalizeUserStatus(values.status) !== "declined";

  return (
    <WorkerAdminFormSection
      title="Right to work"
      icon="mdi-card-account-details-outline"
      description={
        requireRtwEvidence
          ? "Verify documents and compliance checks submitted during onboarding."
          : "Right to Work uploads are optional when declining this worker."
      }
    >
      <Col md={12}>
        <Row className="gy-2">
          <AdminRightToWorkFormFields
            mode={variant}
            rightToWork={values.rightToWork}
            setRtwField={(field, value) =>
              setFieldValue(`rightToWork.${field}`, value)
            }
            showVisaExpiryAdminField={showVisaExpiryAdminField}
            labelClassName={labelClassName}
            homeOfficeFileId={homeOfficeFileId}
            trustIdFileId={trustIdFileId}
            errors={formik.errors?.rightToWork}
            touched={touched}
            submitCount={submitCount}
            requireRtwEvidence={requireRtwEvidence}
          />
        </Row>
      </Col>
    </WorkerAdminFormSection>
  );
}

function AdminWorkerDbsFields({ formik }) {
  const values = formik.values;
  const setFieldValue = formik.setFieldValue.bind(formik);
  const showErrors = (formik.submitCount || 0) > 0;
  const dbsErrors = Array.isArray(formik.errors?.dbs) ? formik.errors.dbs : [];

  if (!Array.isArray(values.dbs) || values.dbs.length === 0) return null;

  return (
    <WorkerAdminFormSection
      title="DBS certificates"
      icon="mdi-shield-check-outline"
      description="Approve each DBS record and complete required verification fields."
    >
      <Col md={12}>
        {values.dbs.map((dbsItem, idx) => (
          <AdminDbsCertFields
            key={dbsItem.dbsSlug || idx}
            dbsItem={dbsItem}
            label={dbsItem.dbsSlug || `DBS #${idx + 1}`}
            rowErrors={
              typeof dbsErrors[idx] === "object" && dbsErrors[idx] !== null
                ? dbsErrors[idx]
                : {}
            }
            showErrors={showErrors}
            onChange={(next) => {
              const newDbs = [...values.dbs];
              newDbs[idx] = next;
              setFieldValue("dbs", newDbs);
            }}
          />
        ))}
      </Col>
    </WorkerAdminFormSection>
  );
}

function AdminWorkerOtherCertsFields({ formik, labelClassName }) {
  const values = formik.values;
  const setFieldValue = formik.setFieldValue.bind(formik);
  const showErrors = (formik.submitCount || 0) > 0;
  const otherCertErrors = Array.isArray(formik.errors?.otherCerts)
    ? formik.errors.otherCerts
    : [];

  if (!Array.isArray(values.otherCerts) || values.otherCerts.length === 0) {
    return null;
  }

  return (
    <WorkerAdminFormSection
      title="Other certificates"
      icon="mdi-certificate-outline"
      description="Set verification status and expiry for additional compliance certificates."
    >
      <Col md={12}>
        <AdminOtherCertsEditor
          certs={values.otherCerts}
          rowErrors={otherCertErrors}
          showErrors={showErrors}
          onChange={(next) => setFieldValue("otherCerts", next)}
          labelClassName={labelClassName}
        />
      </Col>
    </WorkerAdminFormSection>
  );
}

function AdminWorkerDrivingFields({
  formik,
  labelClassName,
  dvlaCodeId,
  hasDrivingLicenseEvidence,
  hasDvlaShareCodeValue,
}) {
  const values = formik.values;
  const setFieldValue = formik.setFieldValue.bind(formik);
  const touched = formik.touched?.rightToWork || {};
  const submitCount = formik.submitCount || 0;

  if (!hasDrivingLicenseEvidence && !hasDvlaShareCodeValue) return null;

  return (
    <WorkerAdminFormSection
      title="Driving licence & DVLA"
      icon="mdi-car-outline"
      description="Verify driving licence and DVLA share code details submitted during onboarding."
    >
      <Col md={12}>
        <Row className="gy-2">
          <AdminDrivingComplianceFormFields
            rightToWork={values.rightToWork}
            setRtwField={(field, value) =>
              setFieldValue(`rightToWork.${field}`, value)
            }
            hasDrivingLicenseEvidence={hasDrivingLicenseEvidence}
            hasDvlaShareCodeValue={hasDvlaShareCodeValue}
            labelClassName={labelClassName}
            dvlaCodeId={dvlaCodeId}
            errors={formik.errors?.rightToWork || {}}
            touched={touched}
            submitCount={submitCount}
          />
        </Row>
      </Col>
    </WorkerAdminFormSection>
  );
}

function WorkerDetails(props) {
  const { slug } = props.router?.params || {};
  const isProfileUpdateRequest = new URLSearchParams(
    props.router?.location?.search || ""
  ).has("updated");
  const { t } = useTranslation();
  const userDetailsMutation = useUserDetails();

  useEffect(() => {
    if (!slug) return;
    userDetailsMutation.mutate(slug);
  }, [slug]);

  const payload =
    userDetailsMutation.data?.data ?? (userDetailsMutation.data || {});
  const user = payload || {};

  const worker = user.worker || {};
  const pi = worker.personalInformation || {};
  const ec = worker.emergencyContact || {};
  const rtw = worker.rightToWork || {};
  const tax = worker.employmentTax || {};
  const bank = worker.bankDetails || {};
  const prefs = worker.workPreferences || {};
  const comp = worker.complianceSkills || {};
  const decs = worker.declarationsConsents || {};
  const may = worker.moreAboutYou || {};

  const createUser = useCreateUser();
  const updateWorker = useUpdateWorkerByAdmin();
  const [pendingCreateRequest, setPendingCreateRequest] = useState(null);

  // pull the email/role from the user-details payload
  const userPayload =
    userDetailsMutation?.data?.data ?? userDetailsMutation?.data ?? {};
  const emailFromData = userPayload?.email || "";
  const roleFromData = userPayload?.role || "";

  // enums → options
  const ROLE_OPTIONS = ["client", "worker"];
  const STATUS_OPTIONS = ["approved", "declined", "actionRequired"];
  const TIER_OPTIONS = ["top", "entry", "lower"];

  // Admin already created the platform user → update flow; otherwise create
  const hasAdminCreated = !!user?.hasAdminCreated;
  // Profile Update Requests: existing users — always update, never create
  const canUpdateWorker = hasAdminCreated || isProfileUpdateRequest;
  const getStatusOptions = () => STATUS_OPTIONS;

  /** Map API values to form options (e.g. legacy `none` tier or `pending` status). */
  const tierValueForForm = (tier) =>
    TIER_OPTIONS.includes(tier) ? tier : "entry";
  const statusValueForForm = (status) => {
    const normalized = normalizeUserStatus(status);
    return STATUS_OPTIONS.includes(normalized) ? normalized : "actionRequired";
  };

  // Helper to convert date to input format
  const toInputDate = (val) => {
    if (!val) return "";
    if (/^\d{4}-\d{2}-\d{2}$/.test(val)) return val; // already YYYY-MM-DD
    const m1 = val.match(/^(\d{2})[-/](\d{2})[-/](\d{4})$/); // DD-MM-YYYY or DD/MM/YYYY
    if (m1) {
      const [, dd, mm, yyyy] = m1;
      return `${yyyy}-${mm}-${dd}`;
    }
    const d = new Date(val);
    if (Number.isNaN(d.getTime())) return "";
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
  };

  // ---------- helpers ----------
  const HUMANIZE_VALUE_MAP = {
    weekdays: "Weekdays",
    weekend: "Weekends",
    nights: "Nights",
    bankHoliday: "Bank holidays",
    urgentCover: "Urgent cover",
    car: "Car",
    public: "Public transport",
    male: "Male",
    female: "Female",
    other: "Other",
    enhanced: "Enhanced",
    standard: "Standard",
    adult: "Adult workforce",
    child: "Child workforce",
    both: "Both workforces",
  };

  const labelize = (s) => {
    if (typeof s !== "string") return s;
    const trimmed = s.trim();
    if (!trimmed) return trimmed;
    if (HUMANIZE_VALUE_MAP[trimmed]) return HUMANIZE_VALUE_MAP[trimmed];
    return trimmed
      .replace(/_/g, " ")
      .replace(/-/g, " ")
      .replace(/([a-z])([A-Z])/g, "$1 $2")
      .replace(/\b\w/g, (c) => c.toUpperCase());
  };

  const humanizeValue = (value) => {
    if (value == null || String(value).trim() === "") return "-";
    return labelize(String(value));
  };

  const humanizeList = (arr) =>
    Array.isArray(arr) && arr.length
      ? arr.map((item) => humanizeValue(item)).join(", ")
      : "-";

  const hasText = (v) => v != null && String(v).trim() !== "";

  const formatDisplayDate = (val) => {
    if (!hasText(val)) return "";
    const d = new Date(val);
    return Number.isNaN(d.getTime()) ? String(val) : d.toLocaleDateString();
  };

  const IMAGE_FILE_EXTENSIONS = new Set([
    "jpg",
    "jpeg",
    "png",
    "gif",
    "webp",
    "bmp",
    "svg",
  ]);
  const VIDEO_FILE_EXTENSIONS = new Set([
    "webm",
    "mp4",
    "mov",
    "mpeg",
    "mpg",
    "ogg",
    "m4v",
  ]);

  const fileExtensionFromPath = (path) => {
    if (!path || typeof path !== "string") return "";
    const noQuery = path.split("?")[0];
    const lastSegment = noQuery.split("/").pop() || "";
    const m = lastSegment.match(/\.([a-zA-Z0-9]+)$/);
    return m ? m[1].toLowerCase() : "";
  };

  const isImagePath = (path) => {
    const ext = fileExtensionFromPath(path);
    return ext !== "" && IMAGE_FILE_EXTENSIONS.has(ext);
  };

  const isVideoPath = (path) => {
    const ext = fileExtensionFromPath(path);
    return ext !== "" && VIDEO_FILE_EXTENSIONS.has(ext);
  };

  const linkAnchorStyle = {
    color: "#007bff",
    textDecoration: "underline",
    cursor: "pointer",
  };

  const linkWithText = (v, text = "Click here to view") => {
    if (typeof v !== "string" || !v.trim()) return "-";
    const url = mediaUrl(v);
    const showImg = isImagePath(v);
    const showVid = isVideoPath(v);
    const anchor = (
      <a href={url} target="_blank" rel="noreferrer" style={linkAnchorStyle}>
        {text}
      </a>
    );
    if (!showImg && !showVid) return anchor;
    return (
      <div className="file-link-with-preview">
        {showImg && (
          <div className="mb-2">
            <img
              src={url}
              alt=""
              className="img-fluid rounded border bg-light"
              loading="lazy"
              style={{
                maxHeight: 100,
                maxWidth: "100%",
                objectFit: "contain",
              }}
            />
          </div>
        )}
        {showVid && (
          <div className="mb-2">
            <video
              src={url}
              controls
              className="w-100 rounded border bg-dark"
              style={{ maxHeight: 320 }}
              preload="metadata"
            />
          </div>
        )}
        <div>{anchor}</div>
      </div>
    );
  };

  const linkList = (arr, baseText = "Click here to view") =>
    Array.isArray(arr) && arr.length
      ? arr.map((u, i) => (
          <div key={i} className={i > 0 ? "mt-3" : undefined}>
            {linkWithText(
              u,
              arr.length > 1 ? `${baseText} #${i + 1}` : baseText,
            )}
          </div>
        ))
      : "-";
  const linkOrDash = (v) =>
    Array.isArray(v) ? linkList(v, "Open") : linkWithText(v, "Open");

  // ✓ / ✗ / –
  const Bool = ({ value }) =>
    value === true ? (
      <span className="text-success" aria-label="Yes">
        ✓
      </span>
    ) : value === false ? (
      <span className="text-danger" aria-label="No">
        ✗
      </span>
    ) : (
      <span className="text-muted" aria-label="Unknown">
        –
      </span>
    );

  /** Pill-style status labels with light shadow and border (read-only UI). */
  const AccentBadge = ({
    variant = "neutral",
    icon,
    children,
    className = "",
  }) => {
    const variantClass =
      variant === "success"
        ? "border-success text-success bg-success bg-opacity-10"
        : variant === "warning"
          ? "border-warning text-dark bg-warning bg-opacity-10"
          : variant === "danger"
            ? "border-danger text-danger bg-danger bg-opacity-10"
            : variant === "info"
              ? "border-info text-info bg-info bg-opacity-10"
              : "border-secondary text-secondary bg-light";

    return (
      <span
        className={`badge rounded-pill border fw-semibold px-3 py-2 d-inline-flex align-items-center gap-1 ${variantClass} ${className}`}
        style={{
          boxShadow: "0 2px 8px rgba(15, 23, 42, 0.08)",
          fontSize: "0.8125rem",
        }}
      >
        {icon ? <i className={`mdi ${icon}`} aria-hidden /> : null}
        {children}
      </span>
    );
  };

  const accentForUserStatus = (status) => {
    if (!status) return { variant: "neutral", label: "-" };
    const normalized = normalizeUserStatus(status);
    const s = String(normalized).toLowerCase();
    if (s === "approved")
      return { variant: "success", label: userStatusLabel(status) };
    if (s === "pending")
      return { variant: "warning", label: userStatusLabel(status) };
    if (s === "declined")
      return { variant: "danger", label: userStatusLabel(status) };
    if (s === "actionrequired")
      return { variant: "info", label: userStatusLabel(status) };
    if (s === "active") return { variant: "success", label: "Active" };
    if (s === "close" || s === "closed")
      return { variant: "danger", label: "Closed" };
    return { variant: "neutral", label: userStatusLabel(status) };
  };

  const accentForTier = (tier) => {
    const t = String(tier || "").toLowerCase();
    if (t === "top") return { variant: "success", label: tier || "-" };
    if (t === "entry") return { variant: "info", label: tier || "-" };
    if (t === "lower") return { variant: "warning", label: tier || "-" };
    if (t === "none") return { variant: "neutral", label: tier || "none" };
    return { variant: "neutral", label: tier || "-" };
  };

  const UserStatusBadge = ({ status }) => {
    const { variant, label } = accentForUserStatus(status);
    return (
      <AccentBadge
        variant={variant}
        icon={
          variant === "success"
            ? "mdi-check-circle-outline"
            : variant === "warning"
              ? "mdi-clock-outline"
              : variant === "danger"
                ? "mdi-close-circle-outline"
                : variant === "info"
                  ? "mdi-file-document-outline"
                  : "mdi-help-circle-outline"
        }
      >
        {label}
      </AccentBadge>
    );
  };

  const OnboardingAccentBadge = ({ done }) =>
    done ? (
      <AccentBadge variant="success" icon="mdi-school-outline">
        Onboarding complete
      </AccentBadge>
    ) : (
      <AccentBadge variant="warning" icon="mdi-school-outline">
        Onboarding pending
      </AccentBadge>
    );

  /**
   * Emergency contact naming:
   * API provides `firstName`, `surName`, and often a non-combined `fullName`.
   * Prefer firstName + surName; fall back to splitting fullName for older payloads.
   */
  const emergencyContactDisplay = useMemo(() => {
    const firstName = ec.firstName?.trim();
    const lastName = (ec.surName ?? ec.lastName)?.trim();
    const rawFull = ec.fullName?.trim();

    if (firstName || lastName) {
      const first = firstName || "";
      const last = lastName || "";
      const combined =
        [first, last].filter(Boolean).join(" ").trim() || rawFull || "";
      return { first, last, combined };
    }

    if (rawFull) {
      const parts = rawFull.split(/\s+/).filter(Boolean);
      const first = parts[0] || "";
      const last = parts.slice(1).join(" ") || "";
      const combined = last ? `${first} ${last}`.trim() : rawFull;
      return { first, last, combined };
    }

    return { first: "", last: "", combined: "" };
  }, [ec.firstName, ec.surName, ec.lastName, ec.fullName]);

  // ---------- sectors (read-only display from worker preferences) ----------
  const initialSectors = useMemo(() => {
    const listRaw = prefs?.sectors;
    if (!Array.isArray(listRaw)) return [];
    return listRaw.map((entry) => {
      const sectorSlug = entry?.sector?.slug || "";
      const sectorTitle = entry?.sector?.title || "";
      const roles = Array.isArray(entry?.roles)
        ? entry.roles.map((r) => ({
            roleSlug: r?.role?.slug || "",
            roleTitle: r?.role?.title || r?.role?.slug || "",
          }))
        : [];
      return { sectorSlug, sectorTitle, roles };
    });
  }, [prefs?.sectors]);

  // Prepare initial values for DBS and other certs from existing data (for create form)
  const initialDbs = useMemo(() => {
    if (!Array.isArray(comp.dbs)) return [];
    return comp.dbs.map((dbsItem) => {
      const row = mapDbsItemFromApi(dbsItem);
      return {
        ...row,
        issueDate: dbsItem.issueDate ? toInputDate(dbsItem.issueDate) : "",
        lastCheckedDate: dbsItem.lastCheckedDate
          ? toInputDate(dbsItem.lastCheckedDate)
          : "",
        expiryDate: dbsItem.expiryDate ? toInputDate(dbsItem.expiryDate) : "",
      };
    });
  }, [comp.dbs]);

  const initialOtherCerts = useMemo(() => {
    if (!Array.isArray(comp.otherCerts)) return [];
    return comp.otherCerts.map((certItem) => ({
      certSlug: certItem.Cert?.slug || "",
      status: certItem.status ?? false,
      expiryDate: certItem.expiryDate ? toInputDate(certItem.expiryDate) : "",
    }));
  }, [comp.otherCerts]);

  const hasDrivingLicenseFront = useMemo(
    () => hasUploadedMediaFile(rtw?.drivingLicense),
    [rtw?.drivingLicense],
  );

  const hasDrivingLicenseBack = useMemo(
    () => hasUploadedMediaFile(rtw?.drivingLicenseBack),
    [rtw?.drivingLicenseBack],
  );

  const hasDrivingLicenseEvidence = useMemo(
    () => hasDrivingLicenseFront || hasDrivingLicenseBack,
    [hasDrivingLicenseFront, hasDrivingLicenseBack],
  );

  const hasDvlaShareCodeValue = useMemo(
    () => !!(rtw?.dvlaShareCode && String(rtw.dvlaShareCode).trim()),
    [rtw?.dvlaShareCode],
  );

  /** Show visa expiry when RTW status is not British / Irish (onboarding parity). */
  const showVisaExpiryAdminField = useMemo(() => {
    const status = String(rtw?.rightToWorkStatus ?? "").trim();
    return status !== RIGHT_TO_WORK_STATUS_BRITISH_IRISH;
  }, [rtw?.rightToWorkStatus]);

  const initialRightToWorkForm = useMemo(() => {
    const tri = (v) => (v === true ? true : v === false ? false : null);
    return {
      homeOfficeFile: null,
      homeOfficeFileUrl: rtw?.homeOfficeFile ?? "",
      rtwStatus: normalizeRtwVerificationStatus(rtw?.rtwStatus),
      trustIdFile: null,
      trustIdFileUrl: rtw?.trustIdFile ?? "",
      drivingLicenseStatus: tri(rtw?.drivingLicenseStatus),
      drivingLicenseExpiryDate: rtw?.drivingLicenseExpiryDate
        ? toInputDate(rtw.drivingLicenseExpiryDate)
        : "",
      visaExpiryDate: rtw?.visaExpiryDate
        ? toInputDate(rtw.visaExpiryDate)
        : "",
      dvlaShareCode: String(rtw?.dvlaShareCode ?? "").trim(),
    };
  }, [
    rtw?.homeOfficeFile,
    rtw?.rtwStatus,
    rtw?.trustIdFile,
    rtw?.drivingLicenseStatus,
    rtw?.drivingLicenseExpiryDate,
    rtw?.visaExpiryDate,
    rtw?.dvlaShareCode,
  ]);

  const buildWorkerRightToWorkPayload = (formRtw) => {
    const dlVerified = formRtw.drivingLicenseStatus === true;
    const out = {
      rtwStatus: formRtw.rtwStatus,
    };

    const dvlaShareCode = String(formRtw?.dvlaShareCode ?? "").trim();
    if (dvlaShareCode) out.dvlaShareCode = dvlaShareCode;

    if (hasDrivingLicenseEvidence) {
      out.drivingLicenseStatus = formRtw.drivingLicenseStatus;
      if (dlVerified && formRtw.drivingLicenseExpiryDate) {
        out.drivingLicenseExpiryDate = formRtw.drivingLicenseExpiryDate;
      }
    } else {
      out.drivingLicenseStatus = null;
    }

    if (showVisaExpiryAdminField && formRtw.visaExpiryDate) {
      out.visaExpiryDate = formRtw.visaExpiryDate;
    }

    return out;
  };

  const adminRtwPdfFileSchema = Yup.mixed()
    .nullable()
    .test("rtw-file-type", "File must be a PDF", (value) =>
      value == null
        ? true
        : value instanceof File
          ? isAdminRtwPdfFile(value)
          : false,
    )
    .test(
      "rtw-file-size",
      `File must be under ${FILE_VALIDATION.MAX_MB}MB`,
      (value) =>
        value == null
          ? true
          : value instanceof File
            ? isUnderFileSizeLimit(value, FILE_VALIDATION.MAX_MB)
            : false,
    );

  const rtwNullableBooleanSchema = Yup.boolean().nullable();

  const adminRightToWorkBaseSchema = Yup.object({
    homeOfficeFile: adminRtwPdfFileSchema,
    homeOfficeFileUrl: Yup.mixed().notRequired(),
    rtwStatus: rtwNullableBooleanSchema,
    trustIdFile: adminRtwPdfFileSchema,
    trustIdFileUrl: Yup.mixed().notRequired(),
    drivingLicenseStatus: rtwNullableBooleanSchema,
    drivingLicenseExpiryDate: Yup.string().when("drivingLicenseStatus", {
      is: (status) => hasDrivingLicenseEvidence && status === true,
      then: (schema) => schema.required("Driving licence expiry is required"),
      otherwise: (schema) => schema.notRequired(),
    }),
    visaExpiryDate: showVisaExpiryAdminField
      ? Yup.string().required("Visa expiry is required")
      : Yup.string().notRequired(),
    dvlaShareCode: Yup.string(),
  });

  /** Home Office / TrustID evidence required except when declining. */
  const adminRightToWorkSchema = Yup.object().when("status", {
    is: (status) => normalizeUserStatus(status) === "declined",
    then: () => adminRightToWorkBaseSchema,
    otherwise: () =>
      adminRightToWorkBaseSchema.test(
        "rtw-evidence",
        "Provide either a Home Office file or a TrustID file",
        (value) => hasAdminRtwEvidence(value || {}),
      ),
  });

  const initialExperienceLevel = useMemo(() => {
    const raw = pi.experienceLevel ?? user.experienceLevel ?? "";
    return WORKER_EXPERIENCE_LEVEL_OPTIONS.includes(raw) ? raw : "";
  }, [pi.experienceLevel, user.experienceLevel]);

  const workerProfileFieldsSchema = {
    experienceLevel: Yup.string()
      .oneOf(["", ...WORKER_EXPERIENCE_LEVEL_OPTIONS])
      .nullable(),
  };

  const actionRequiredReasonSchema = {
    reason: Yup.string()
      .trim()
      .max(2000, "Reason is too long")
      .when("status", {
        is: (status) => normalizeUserStatus(status) === "actionRequired",
        then: (schema) =>
          schema.required("Reason is required when action is required"),
        otherwise: (schema) => schema.notRequired(),
      }),
  };

  const reasonFromData = typeof user?.reason === "string" ? user.reason : "";

  // ---------- Formik (reinitialize when data arrives) ----------
  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      email: emailFromData,
      role: roleFromData,
      status: statusValueForForm(user.status || "actionRequired"),
      tier: tierValueForForm(user.tier || "entry"),
      reason: reasonFromData,
      experienceLevel: initialExperienceLevel,
      dbs: initialDbs,
      otherCerts: initialOtherCerts,
      rightToWork: initialRightToWorkForm,
    },
    validationSchema: Yup.object({
      email: Yup.string().email("Invalid email").required("Email is required"),
      role: Yup.mixed().oneOf(ROLE_OPTIONS).required("Role is required"),
      status: Yup.mixed()
        .oneOf(getStatusOptions())
        .required("Status is required"),
      tier: Yup.mixed().oneOf(TIER_OPTIONS).required("Tier is required"),
      ...actionRequiredReasonSchema,
      ...workerProfileFieldsSchema,
      rightToWork: adminRightToWorkSchema,
      dbs: Yup.array().of(adminDbsRowSchema),
      otherCerts: Yup.array().of(adminOtherCertRowSchema),
    }),
    onSubmit: (values) => {
      const {
        email,
        role,
        status,
        tier,
        reason,
        experienceLevel,
        dbs,
        otherCerts,
        rightToWork,
      } = values;

      const dbsPayload = buildDbsCompliancePayload(dbs);
      const otherCertsPayload = buildOtherCertsCompliancePayload(otherCerts);

      const workerPayload = {
        personalInformation: buildWorkerPersonalInformationPayload({
          experienceLevel,
        }),
        complianceSkills: {
          ...(dbsPayload.length > 0 ? { dbs: dbsPayload } : {}),
          ...(otherCertsPayload.length > 0
            ? { otherCerts: otherCertsPayload }
            : {}),
        },
        rightToWork: buildWorkerRightToWorkPayload(rightToWork),
      };

      const request = buildAdminWorkerMutationRequest({
        email,
        role,
        status,
        tier,
        reason,
        worker: workerPayload,
        homeOfficeFile: rightToWork.homeOfficeFile,
        trustIdFile: rightToWork.trustIdFile,
      });

      setPendingCreateRequest(request);
    },
  });

  const closeCreateConfirmation = () => {
    if (!createUser.isPending) {
      setPendingCreateRequest(null);
    }
  };

  const confirmCreateWorker = () => {
    if (!pendingCreateRequest || createUser.isPending) return;

    const isDeclineCreate =
      normalizeUserStatus(
        pendingCreateRequest?.payload?.status ??
          (pendingCreateRequest?.data instanceof FormData
            ? pendingCreateRequest.data.get("status")
            : ""),
      ) === "declined";

    createUser.mutate(pendingCreateRequest as any, {
      onSuccess: () => {
        setPendingCreateRequest(null);
        if (isDeclineCreate) {
          toastr.success("User declined successfully!", "", {
            toastClass: "toast-error",
            iconClass: "toast-error",
          });
        } else {
          toastr.success("User created successfully!");
        }
        props.router.navigate("/user/list");
      },
      onError: (error: any) => {
        const errorData = error?.response?.data;
        let errorMessage = isDeclineCreate
          ? "Failed to decline user."
          : "Failed to create user.";

        if (errorData) {
          if (typeof errorData.message === "string") {
            errorMessage = errorData.message;
          } else if (errorData.message?.error) {
            if (Array.isArray(errorData.message.error)) {
              errorMessage = errorData.message.error.join(", ");
            } else if (typeof errorData.message.error === "string") {
              errorMessage = errorData.message.error;
            }
          }
        }

        toastr.error(errorMessage);
      },
    });
  };

  /** -------- Formik: Update (approved / action required workers) -------- */
  const updateFormik = useFormik({
    enableReinitialize: true,
    initialValues: {
      status: statusValueForForm(user.status || "actionRequired"),
      tier: tierValueForForm(user.tier || "entry"),
      reason: reasonFromData,
      experienceLevel: initialExperienceLevel,
      dbs: initialDbs,
      otherCerts: initialOtherCerts,
      rightToWork: initialRightToWorkForm,
    },
    validationSchema: Yup.object({
      status: Yup.mixed()
        .oneOf(getStatusOptions())
        .required("Status is required"),
      tier: Yup.mixed().oneOf(TIER_OPTIONS).required("Tier is required"),
      ...actionRequiredReasonSchema,
      ...workerProfileFieldsSchema,
      rightToWork: adminRightToWorkSchema,
      dbs: Yup.array().of(adminDbsRowSchema),
      otherCerts: Yup.array().of(adminOtherCertRowSchema),
    }),
    onSubmit: (values) => {
      const {
        status,
        tier,
        reason,
        experienceLevel,
        dbs,
        otherCerts,
        rightToWork,
      } = values;

      const dbsPayload = buildDbsCompliancePayload(dbs);
      const otherCertsPayload = buildOtherCertsCompliancePayload(otherCerts);

      const workerPayload = {
        personalInformation: buildWorkerPersonalInformationPayload({
          experienceLevel,
        }),
        complianceSkills: {
          ...(dbsPayload.length > 0 ? { dbs: dbsPayload } : {}),
          ...(otherCertsPayload.length > 0
            ? { otherCerts: otherCertsPayload }
            : {}),
        },
        rightToWork: buildWorkerRightToWorkPayload(rightToWork),
      };

      const request = buildAdminWorkerMutationRequest({
        slug,
        status,
        tier,
        reason,
        worker: workerPayload,
        homeOfficeFile: rightToWork.homeOfficeFile,
        trustIdFile: rightToWork.trustIdFile,
      });

      updateWorker.mutate(
        {
          ...request,
          ...(isProfileUpdateRequest ? { fieldStatus: "updated" } : {}),
        } as any,
        {
          onSuccess: () => {
            toastr.success(
              isProfileUpdateRequest
                ? "User updated successfully!"
                : "Worker updated successfully!"
            );
            if (isProfileUpdateRequest) {
              props.router.navigate("/user/list?updated=true");
            } else {
              userDetailsMutation.mutate(slug);
            }
          },
          onError: (error: any) => {
            const errorData = error?.response?.data;
            let errorMessage = "Failed to update worker.";

            if (errorData) {
              if (typeof errorData.message === "string") {
                errorMessage = errorData.message;
              } else if (errorData.message?.error) {
                if (Array.isArray(errorData.message.error)) {
                  errorMessage = errorData.message.error.join(", ");
                } else if (typeof errorData.message.error === "string") {
                  errorMessage = errorData.message.error;
                }
              }
            }

            toastr.error(errorMessage);
          },
        }
      );
    },
  });

  const sectorsParsedForView = useMemo(() => {
    if (!Array.isArray(initialSectors)) return [];
    return initialSectors.map((s) => ({
      sectorTitle: s.sectorTitle || "-",
      roleTitles: Array.isArray(s.roles)
        ? s.roles.map((r) => r.roleTitle).filter(Boolean)
        : [],
    }));
  }, [initialSectors]);

  const moreAboutYouHealthText =
    may.healthAccessibilityAdjustments || may.healthAccessibilityDetails || "";

  const workHistoryText =
    may.aboutYouAndWorkHistory || may.aboutYouWorkHistory || "";

  const experienceLevelText = hasText(pi.experienceLevel)
    ? String(pi.experienceLevel)
    : hasText(user.experienceLevel)
      ? String(user.experienceLevel)
      : "";

  const hasMoreAboutYouSection =
    hasText(moreAboutYouHealthText) ||
    may.hasUnspentConvictionsOrPendingCases !== undefined ||
    hasText(may.criminalConvictionsDetails);

  const hasWorkHistorySection = hasText(workHistoryText);

  const hasPreferencesSection =
    (Array.isArray(sectorsParsedForView) && sectorsParsedForView.length > 0) ||
    (prefs.preferredWorkRadius !== undefined &&
      prefs.preferredWorkRadius !== null &&
      prefs.preferredWorkRadius !== "") ||
    (Array.isArray(prefs.availability) && prefs.availability.length > 0) ||
    (Array.isArray(prefs.languages) && prefs.languages.length > 0) ||
    (Array.isArray(prefs.transport) && prefs.transport.length > 0) ||
    (prefs.optOut48HourLimit !== undefined &&
      prefs.optOut48HourLimit !== null) ||
    (Array.isArray(prefs.whatMattersMost) && prefs.whatMattersMost.length > 0);

  const hasComplianceDrivingSection =
    hasDrivingLicenseEvidence ||
    rtw.drivingLicenseStatus !== undefined ||
    rtw.drivingLicenseExpiryDate ||
    (rtw.dvlaShareCode && String(rtw.dvlaShareCode).trim()) ||
    (rtw.licenseNumber && String(rtw.licenseNumber).trim());

  const hasDbsCertsSection =
    (Array.isArray(comp.dbs) && comp.dbs.length > 0) ||
    (Array.isArray(comp.otherCerts) && comp.otherCerts.length > 0);

  const hasAdminDbsSection = initialDbs.length > 0;
  const hasAdminOtherCertsSection = initialOtherCerts.length > 0;

  // Accordion states
  const [openPersonal, setOpenPersonal] = useState(true);
  const [openEmergency, setOpenEmergency] = useState(true);
  const [openRightToWork, setOpenRightToWork] = useState(true);
  const [openAdminRtw, setOpenAdminRtw] = useState(true);
  const [openEmployment, setOpenEmployment] = useState(true);
  const [openBank, setOpenBank] = useState(true);
  const [openMoreAboutYou, setOpenMoreAboutYou] = useState(true);
  const [openWorkHistory, setOpenWorkHistory] = useState(true);
  const [openAdminExperience, setOpenAdminExperience] = useState(true);
  const [openWorkPrefs, setOpenWorkPrefs] = useState(true);
  const [openCompliance, setOpenCompliance] = useState(true);
  const [openAdminDriving, setOpenAdminDriving] = useState(true);
  const [openAdminDbs, setOpenAdminDbs] = useState(true);
  const [openAdminOtherCerts, setOpenAdminOtherCerts] = useState(true);
  const [openDeclarations, setOpenDeclarations] = useState(true);
  const [openAdminAccount, setOpenAdminAccount] = useState(true);

  const adminVariant = canUpdateWorker ? "update" : "create";
  const activeFormik = canUpdateWorker ? updateFormik : formik;
  const isDecliningWorker =
    normalizeUserStatus(activeFormik.values.status) === "declined";
  const adminFieldProps = {
    variant: adminVariant,
    formik: activeFormik,
    labelClassName: canUpdateWorker ? "form-label fw-bolder" : "form-label",
    homeOfficeFileId: canUpdateWorker
      ? "rtw_home_office_file_u"
      : "rtw_home_office_file",
    dvlaCodeId: canUpdateWorker ? "rtw_dvla_code_u" : "rtw_dvla_code",
    trustIdFileId: canUpdateWorker
      ? "rtw_trust_id_file_u"
      : "rtw_trust_id_file",
    showVisaExpiryAdminField,
    hasDrivingLicenseEvidence,
    hasDvlaShareCodeValue,
    statusOptions: getStatusOptions(),
    roleOptions: ROLE_OPTIONS,
    tierOptions: TIER_OPTIONS,
  };

  if (userDetailsMutation.isPending) {
    return (
      <div className="page-content">
        <div className="container-fluid">
          <Breadcrumbs title="Users" breadcrumbItem="User Details" />
          <div className="text-muted">Loading user…</div>
        </div>
      </div>
    );
  }

  if (userDetailsMutation.error) {
    return (
      <div className="page-content">
        <div className="container-fluid">
          <Breadcrumbs title="Users" breadcrumbItem="User Details" />
          <div className="text-danger">Failed to load user.</div>
        </div>
      </div>
    );
  }

  // Worker QR download — hidden while QR UI is disabled
  // const downloadQRCode = async () => {
  //   const qrUrl = mediaUrl(worker.qrPng);
  //   if (!qrUrl) return;
  //
  //   try {
  //     const response = await fetch(qrUrl);
  //     const blob = await response.blob();
  //     const url = window.URL.createObjectURL(blob);
  //     const link = document.createElement("a");
  //     link.href = url;
  //     link.download = `worker-qr-code-${user?.slug || user?.id || "qr"}.png`;
  //     document.body.appendChild(link);
  //     link.click();
  //     document.body.removeChild(link);
  //     window.URL.revokeObjectURL(url);
  //   } catch (error) {
  //     console.error("Error downloading QR code:", error);
  //   }
  // };

  return (
    <div className="page-content">
      <div className="container-fluid">
        <Breadcrumbs title="Users" breadcrumbItem="Worker Details" />

        {/* Worker QR UI hidden
        {worker.qrPng && (
          <Card className="border-0 mb-3">
            <CardBody className="d-flex align-items-center justify-content-between p-4">
              <div className="d-flex align-items-center gap-3">
                <div
                  style={{
                    width: "120px",
                    height: "120px",
                    border: "2px solid #e9ecef",
                    borderRadius: "8px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    backgroundColor: "#f8f9fa",
                    padding: "8px",
                  }}
                >
                  <img
                    src={mediaUrl(worker.qrPng)}
                    alt="Worker QR Code"
                    style={{
                      maxWidth: "100%",
                      maxHeight: "100%",
                      objectFit: "contain",
                    }}
                  />
                </div>
                <div>
                  <h6 className="mb-1 fw-bold">Worker QR Code</h6>
                  <small className="text-muted">
                    Scan this QR code to view worker profile
                  </small>
                </div>
              </div>
              <Button color="primary" onClick={downloadQRCode}>
                <i className="mdi mdi-download me-2"></i>
                Download QR Code
              </Button>
            </CardBody>
          </Card>
        )}
        */}

        {/* Name banner */}
        <Card className="border-0 mb-3">
          <CardBody className="bg-primary text-white py-4 px-3 rounded">
            <h5 className="mb-0 text-white">
              {user?.fullName || user?.email || "Worker"}
            </h5>
          </CardBody>
        </Card>

        <Form
          onSubmit={(e) => activeFormik.handleSubmit(e as any)}
        >
          {/* Account summary (read-only) */}
          <Card className="border-0 shadow-sm mb-3">
            <CardBody className="py-3">
              <Row className="gy-3 align-items-center">
                <Col xs="auto">
                  <small className="text-muted fw-bolder d-block mb-1">
                    Status
                  </small>
                  <UserStatusBadge status={user?.status} />
                  {normalizeUserStatus(user?.status) === "declined" &&
                    typeof user?.reason === "string" &&
                    user.reason.trim() && (
                      <p className="small text-muted mb-0 mt-2">
                        <span className="fw-bolder">Reason:</span>{" "}
                        {user.reason.trim()}
                      </p>
                    )}
                </Col>
                <Col xs="auto">
                  <small className="text-muted fw-bolder d-block mb-1">
                    Tier
                  </small>
                  {(() => {
                    const { variant, label } = accentForTier(user?.tier);
                    return (
                      <AccentBadge
                        variant={variant}
                        icon="mdi-layers-triple-outline"
                      >
                        {label}
                      </AccentBadge>
                    );
                  })()}
                </Col>
                <Col xs="auto">
                  <small className="text-muted fw-bolder d-block mb-1">
                    Onboarding
                  </small>
                  <OnboardingAccentBadge
                    done={!!user?.hasCompletedOnBoarding}
                  />
                </Col>
                <Col xs="auto">
                  <small className="text-muted fw-bolder d-block mb-1">
                    Verified
                  </small>
                  {(() => {
                    const rtwVerified = normalizeRtwVerificationStatus(
                      rtw?.rtwStatus,
                    );
                    if (rtwVerified === true) {
                      return (
                        <AccentBadge
                          variant="success"
                          icon="mdi-shield-check-outline"
                        >
                          Yes
                        </AccentBadge>
                      );
                    }
                    if (rtwVerified === false) {
                      return (
                        <AccentBadge
                          variant="neutral"
                          icon="mdi-shield-off-outline"
                        >
                          No
                        </AccentBadge>
                      );
                    }
                    return (
                      <AccentBadge
                        variant="neutral"
                        icon="mdi-shield-off-outline"
                      >
                        Not set
                      </AccentBadge>
                    );
                  })()}
                </Col>
                {hasText(user?.contractSignedAt) && (
                  <Col xs="auto">
                    <small className="text-muted fw-bolder d-block mb-1">
                      Contract signed
                    </small>
                    <div>{formatDisplayDate(user.contractSignedAt)}</div>
                  </Col>
                )}
                {Array.isArray(worker?.badges) && worker.badges.length > 0 && (
                  <Col xs={12} md="auto">
                    <small className="text-muted fw-bolder d-block mb-1">
                      Badges
                    </small>
                    <div className="d-flex flex-wrap gap-2">
                      {worker.badges.map((badge) => (
                        <AccentBadge
                          key={badge}
                          variant="info"
                          icon="mdi-certificate-outline"
                        >
                          {badge}
                        </AccentBadge>
                      ))}
                    </div>
                  </Col>
                )}
              </Row>
            </CardBody>
          </Card>

          {/* ===== 1. Personal Information ===== */}
          <Card className="mb-3">
            <CardHeader
              onClick={() => setOpenPersonal((v) => !v)}
              className="d-flex justify-content-between align-items-center"
              style={{ cursor: "pointer", backgroundColor: "#ededed" }}
            >
              <h5 className="mb-0">Personal Information</h5>
              <i
                className={`mdi mdi-chevron-${
                  openPersonal ? "up" : "down"
                } bg-dark text-white rounded-circle`}
                style={{
                  width: "20px",
                  height: "20px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "12px",
                }}
              ></i>
            </CardHeader>
            <Collapse isOpen={openPersonal}>
              <CardBody>
                <Row className="gy-2">
                  {(pi.firstName || user.fullName) && (
                    <Col md={4}>
                      <small className="text-muted fw-bolder">First Name</small>
                      <div>
                        {pi.firstName ||
                          (user.fullName && user.fullName.split(/\s+/)[0]) ||
                          "-"}
                      </div>
                    </Col>
                  )}
                  {(pi.lastName || user.fullName) && (
                    <Col md={4}>
                      <small className="text-muted fw-bolder">Last Name</small>
                      <div>
                        {pi.lastName ||
                          (user.fullName &&
                            user.fullName.split(/\s+/).slice(1).join(" ")) ||
                          "-"}
                      </div>
                    </Col>
                  )}
                  {user.fullName && (
                    <Col md={4}>
                      <small className="text-muted fw-bolder">Full Name</small>
                      <div>{user.fullName}</div>
                    </Col>
                  )}
                  {pi.dob && (
                    <Col md={4}>
                      <small className="text-muted fw-bolder">
                        Date of birth
                      </small>
                      <div>{pi.dob}</div>
                    </Col>
                  )}
                  {pi.gender && (
                    <Col md={4}>
                      <small className="text-muted fw-bolder">Gender</small>
                      <div>{humanizeValue(pi.gender)}</div>
                    </Col>
                  )}
                  {user.phoneNumber && (
                    <Col md={4}>
                      <small className="text-muted fw-bolder">Phone</small>
                      <div>{user.phoneNumber}</div>
                    </Col>
                  )}
                  {user.email && (
                    <Col md={4}>
                      <small className="text-muted fw-bolder">Email</small>
                      <div>{user.email}</div>
                    </Col>
                  )}
                  {pi.currentAddress && (
                    <Col md={4}>
                      <small className="text-muted fw-bolder">
                        Current Address
                      </small>
                      <div>{pi.currentAddress}</div>
                    </Col>
                  )}
                  {hasText(pi.addressLine1) && (
                    <Col md={4}>
                      <small className="text-muted fw-bolder">
                        Address line 1
                      </small>
                      <div>{pi.addressLine1}</div>
                    </Col>
                  )}
                  {hasText(pi.addressLine2) && (
                    <Col md={4}>
                      <small className="text-muted fw-bolder">
                        Address line 2
                      </small>
                      <div>{pi.addressLine2}</div>
                    </Col>
                  )}
                  {hasText(pi.city) && (
                    <Col md={4}>
                      <small className="text-muted fw-bolder">City</small>
                      <div>{pi.city}</div>
                    </Col>
                  )}
                  {hasText(pi.county) && (
                    <Col md={4}>
                      <small className="text-muted fw-bolder">County</small>
                      <div>{pi.county}</div>
                    </Col>
                  )}
                  {hasText(pi.country) && (
                    <Col md={4}>
                      <small className="text-muted fw-bolder">Country</small>
                      <div>{pi.country}</div>
                    </Col>
                  )}
                  {(hasText(pi.postalCode) || hasText(pi.postcode)) && (
                    <Col md={4}>
                      <small className="text-muted fw-bolder">Postcode</small>
                      <div>{pi.postalCode || pi.postcode}</div>
                    </Col>
                  )}
                  {hasText(pi.nationality) && (
                    <Col md={4}>
                      <small className="text-muted fw-bolder">
                        Nationality
                      </small>
                      <div>{pi.nationality}</div>
                    </Col>
                  )}
                  {pi.nationalInsuranceNumber && (
                    <Col md={4}>
                      <small className="text-muted fw-bolder">
                        National Insurance number
                      </small>
                      <div>{pi.nationalInsuranceNumber}</div>
                    </Col>
                  )}
                  {pi.baseLocation && pi.baseLocation.coordinates && (
                    <Col md={4}>
                      <small className="text-muted fw-bolder">
                        Base Location
                      </small>
                      <div>
                        {`${pi.baseLocation.coordinates[1]}, ${pi.baseLocation.coordinates[0]}`}
                      </div>
                    </Col>
                  )}
                  {(() => {
                    const hasPiSelfie =
                      pi.uploadSelfie &&
                      (Array.isArray(pi.uploadSelfie)
                        ? pi.uploadSelfie.length > 0
                        : String(pi.uploadSelfie).trim());
                    const hasRtwSelfie =
                      rtw.uploadSelfie &&
                      (Array.isArray(rtw.uploadSelfie)
                        ? rtw.uploadSelfie.length > 0
                        : String(rtw.uploadSelfie).trim());
                    if (!hasPiSelfie && !hasRtwSelfie) return null;
                    const selfieUrl =
                      Array.isArray(pi.uploadSelfie) &&
                      pi.uploadSelfie.length > 0
                        ? pi.uploadSelfie
                        : typeof pi.uploadSelfie === "string" &&
                            pi.uploadSelfie.trim()
                          ? pi.uploadSelfie
                          : Array.isArray(rtw.uploadSelfie) &&
                              rtw.uploadSelfie.length > 0
                            ? rtw.uploadSelfie
                            : rtw.uploadSelfie;
                    const isArray = Array.isArray(selfieUrl);
                    return (
                      <Col md={4}>
                        <small className="text-muted fw-bolder d-block mb-2">
                          Selfie (JobArk ID)
                        </small>
                        <div>
                          {isArray
                            ? linkList(selfieUrl, "View selfie")
                            : linkWithText(selfieUrl, "View selfie")}
                        </div>
                      </Col>
                    );
                  })()}
                </Row>
              </CardBody>
            </Collapse>
          </Card>

          {/* ===== 2. Emergency Contact ===== */}
          <Card className="mb-3">
            <CardHeader
              onClick={() => setOpenEmergency((v) => !v)}
              className="d-flex justify-content-between align-items-center"
              style={{ cursor: "pointer", backgroundColor: "#ededed" }}
            >
              <h5 className="mb-0">Emergency Contact</h5>
              <i
                className={`mdi mdi-chevron-${
                  openEmergency ? "up" : "down"
                } bg-dark text-white rounded-circle`}
                style={{
                  width: "20px",
                  height: "20px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "12px",
                }}
              ></i>
            </CardHeader>
            <Collapse isOpen={openEmergency}>
              <CardBody>
                <Row className="gy-2">
                  {emergencyContactDisplay.first && (
                    <Col md={4}>
                      <small className="text-muted fw-bolder">First Name</small>
                      <div>{emergencyContactDisplay.first}</div>
                    </Col>
                  )}
                  {emergencyContactDisplay.last && (
                    <Col md={4}>
                      <small className="text-muted fw-bolder">Last Name</small>
                      <div>{emergencyContactDisplay.last}</div>
                    </Col>
                  )}
                  {emergencyContactDisplay.combined && (
                    <Col md={4}>
                      <small className="text-muted fw-bolder">Full Name</small>
                      <div>{emergencyContactDisplay.combined}</div>
                    </Col>
                  )}
                  {(ec.relationshipToWorker || ec.relationship) && (
                    <Col md={4}>
                      <small className="text-muted fw-bolder">
                        Relationship
                      </small>
                      <div>{ec.relationshipToWorker || ec.relationship}</div>
                    </Col>
                  )}
                  {ec.phoneNumber && (
                    <Col md={4}>
                      <small className="text-muted fw-bolder">Phone</small>
                      <div>{ec.phoneNumber}</div>
                    </Col>
                  )}
                  {(ec.alternativePhoneNumber || ec.altPhoneNumber) && (
                    <Col md={4}>
                      <small className="text-muted fw-bolder">
                        Alternative phone
                      </small>
                      <div>
                        {ec.alternativePhoneNumber || ec.altPhoneNumber}
                      </div>
                    </Col>
                  )}
                  {ec.email && (
                    <Col md={4}>
                      <small className="text-muted fw-bolder">Email</small>
                      <div>{ec.email}</div>
                    </Col>
                  )}
                  {ec.address && (
                    <Col md={4}>
                      <small className="text-muted fw-bolder">Address</small>
                      <div>{ec.address}</div>
                    </Col>
                  )}
                </Row>
              </CardBody>
            </Collapse>
          </Card>

          {/* ===== 3. Right to Work ===== */}
          <Card className="mb-3">
            <CardHeader
              onClick={() => setOpenRightToWork((v) => !v)}
              className="d-flex justify-content-between align-items-center"
              style={{ cursor: "pointer", backgroundColor: "#ededed" }}
            >
              <h5 className="mb-0">Right to Work</h5>
              <i
                className={`mdi mdi-chevron-${
                  openRightToWork ? "up" : "down"
                } bg-dark text-white rounded-circle`}
                style={{
                  width: "20px",
                  height: "20px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "12px",
                }}
              ></i>
            </CardHeader>
            <Collapse isOpen={openRightToWork}>
              <CardBody>
                <Row className="gy-2">
                  <Col md={12}>
                    <div className="worker-admin-rtw-group mb-2">
                      <p className="worker-admin-rtw-group__label">
                        {t("proofOfAddressAcceptableAdminTitle")}
                      </p>
                      <ProofOfAddressAcceptableContent
                        showIntro={false}
                        className="small text-muted"
                      />
                    </div>
                  </Col>
                  {rtw.rightToWorkInUk !== undefined &&
                    rtw.rightToWorkInUk !== null && (
                      <Col md={4}>
                        <small className="text-muted fw-bolder">
                          Right to work in UK
                        </small>
                        <div>
                          <Bool value={rtw.rightToWorkInUk} />
                        </div>
                      </Col>
                    )}
                  {rtw.rightToWorkStatus &&
                    String(rtw.rightToWorkStatus).trim() &&
                    String(rtw.rightToWorkStatus).trim() !==
                      RIGHT_TO_WORK_STATUS_BRITISH_IRISH && (
                      <Col md={4}>
                        <small className="text-muted fw-bolder">
                          Right to work status
                        </small>
                        <div>{String(rtw.rightToWorkStatus)}</div>
                      </Col>
                    )}
                  {rtw.homeShareSortCode &&
                    String(rtw.homeShareSortCode).trim() && (
                      <Col md={4}>
                        <small className="text-muted fw-bolder">
                          Home Office share code
                        </small>
                        <div>{String(rtw.homeShareSortCode).trim()}</div>
                      </Col>
                    )}
                  {rtw.nationality &&
                    rtw.nationality.trim() &&
                    rtw.nationality !== "-" && (
                      <Col md={4}>
                        <small className="text-muted fw-bolder">
                          Nationality
                        </small>
                        <div>{rtw.nationality}</div>
                      </Col>
                    )}
                  {rtw.visaExpiryDate && (
                    <Col md={4}>
                      <small className="text-muted fw-bolder">
                        Visa expiry date
                      </small>
                      <div>
                        {new Date(rtw.visaExpiryDate).toLocaleDateString()}
                      </div>
                    </Col>
                  )}
                  {rtw.visaImmigrationStatus &&
                    rtw.visaImmigrationStatus.trim() &&
                    rtw.visaImmigrationStatus !== "-" && (
                      <Col md={4}>
                        <small className="text-muted fw-bolder">
                          Visa or immigration status
                        </small>
                        <div>{labelize(rtw.visaImmigrationStatus)}</div>
                      </Col>
                    )}
                  {((Array.isArray(rtw.uploadId) && rtw.uploadId.length > 0) ||
                    (typeof rtw.uploadId === "string" &&
                      rtw.uploadId.trim())) && (
                    <Col md={4}>
                      <small className="text-muted fw-bolder d-block mb-2">
                        Photo ID
                      </small>
                      <small className="text-muted d-block mb-1">
                        Passport or driving licence accepted.
                      </small>
                      <div>
                        {Array.isArray(rtw.uploadId)
                          ? linkList(rtw.uploadId, "Click here to view")
                          : linkWithText(rtw.uploadId, "Click here to view")}
                      </div>
                    </Col>
                  )}
                  {((Array.isArray(rtw.uploadSelfie) &&
                    rtw.uploadSelfie.length > 0) ||
                    (typeof rtw.uploadSelfie === "string" &&
                      rtw.uploadSelfie.trim())) && (
                    <Col md={4}>
                      <small className="text-muted fw-bolder d-block mb-2">
                        Live selfie
                      </small>
                      <div>
                        {Array.isArray(rtw.uploadSelfie)
                          ? linkList(rtw.uploadSelfie, "Click here to view")
                          : linkWithText(
                              rtw.uploadSelfie,
                              "Click here to view",
                            )}
                      </div>
                    </Col>
                  )}
                  {((Array.isArray(rtw.proofOfAddress) &&
                    rtw.proofOfAddress.length > 0) ||
                    (typeof rtw.proofOfAddress === "string" &&
                      rtw.proofOfAddress.trim())) && (
                    <Col md={4}>
                      <small className="text-muted fw-bolder d-block mb-2">
                        Proof of Address
                      </small>
                      <div>
                        {Array.isArray(rtw.proofOfAddress)
                          ? linkList(rtw.proofOfAddress, "Click here to view")
                          : linkWithText(
                              rtw.proofOfAddress,
                              "Click here to view",
                            )}
                      </div>
                    </Col>
                  )}
                  {rtw.consentTrustId !== undefined &&
                    rtw.consentTrustId !== null && (
                      <Col md={4}>
                        <small className="text-muted fw-bolder">
                          Consent to TrustID
                        </small>
                        <div>
                          <Bool value={rtw.consentTrustId} />
                        </div>
                      </Col>
                    )}
                  {formatRtwVerificationStatus(rtw.rtwStatus) && (
                    <Col md={4}>
                      <small className="text-muted fw-bolder d-block mb-2">
                        Status
                      </small>
                      <div>{formatRtwVerificationStatus(rtw.rtwStatus)}</div>
                    </Col>
                  )}
                  {hasUploadedMediaFile(rtw.homeOfficeFile) && (
                    <Col md={4}>
                      <small className="text-muted fw-bolder d-block mb-2">
                        Home Office File
                      </small>
                      <div>{linkOrDash(rtw.homeOfficeFile)}</div>
                    </Col>
                  )}
                  {hasUploadedMediaFile(rtw.trustIdFile) && (
                    <Col md={4}>
                      <small className="text-muted fw-bolder d-block mb-2">
                        TrustID file
                      </small>
                      <div>{linkOrDash(rtw.trustIdFile)}</div>
                    </Col>
                  )}
                  {rtw.onscreeningVideo &&
                    String(rtw.onscreeningVideo).trim() && (
                      <Col md={12}>
                        <small className="text-muted fw-bolder d-block mb-2">
                          Onscreening video
                        </small>
                        <div>
                          {linkWithText(rtw.onscreeningVideo, "View video")}
                        </div>
                      </Col>
                    )}
                </Row>
              </CardBody>
            </Collapse>
          </Card>

          <WorkerDetailsCollapsibleCard
            title="Admin review"
            isOpen={openAdminRtw}
            onToggle={setOpenAdminRtw}
          >
            <AdminWorkerRightToWorkFields {...adminFieldProps} />
          </WorkerDetailsCollapsibleCard>

          {/* ===== 4. Employment & Tax ===== */}
          <Card className="mb-3">
            <CardHeader
              onClick={() => setOpenEmployment((v) => !v)}
              className="d-flex justify-content-between align-items-center"
              style={{ cursor: "pointer", backgroundColor: "#ededed" }}
            >
              <h5 className="mb-0">Employment & Tax</h5>
              <i
                className={`mdi mdi-chevron-${
                  openEmployment ? "up" : "down"
                } bg-dark text-white rounded-circle`}
                style={{
                  width: "20px",
                  height: "20px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "12px",
                }}
              ></i>
            </CardHeader>
            <Collapse isOpen={openEmployment}>
              <CardBody>
                <Row className="gy-2">
                  {tax.hasP45 !== undefined && tax.hasP45 !== null && (
                    <Col md={4}>
                      <small className="text-muted fw-bolder">Has a P45</small>
                      <div>
                        <Bool value={tax.hasP45} />
                      </div>
                    </Col>
                  )}
                  {!tax.hasP45 && tax.starterChecklistChoice && (
                    <Col md={12}>
                      <small className="text-muted fw-bolder">
                        Selected option
                      </small>
                      <div>
                        <StarterChecklistChoiceDisplay
                          value={tax.starterChecklistChoice}
                        />
                      </div>
                    </Col>
                  )}
                  {!tax.hasP45 &&
                    tax.hasPostgraduateLoan !== undefined &&
                    tax.hasPostgraduateLoan !== null && (
                      <Col md={4}>
                        <small className="text-muted fw-bolder">
                          Student or postgraduate loan
                        </small>
                        <div>
                          <Bool value={tax.hasPostgraduateLoan} />
                        </div>
                      </Col>
                    )}
                  {!tax.hasP45 && tax.hasPostgraduateLoan && tax.planType && (
                    <Col md={4}>
                      <small className="text-muted fw-bolder">Plan type</small>
                      <div>{labelStudentLoanPlanType(tax.planType)}</div>
                    </Col>
                  )}
                  {tax.acceptEmergencyTaxCode !== undefined &&
                    tax.acceptEmergencyTaxCode !== null && (
                      <Col md={4}>
                        <small className="text-muted fw-bolder">
                          Accepts emergency tax code
                        </small>
                        <div>
                          <Bool value={tax.acceptEmergencyTaxCode} />
                        </div>
                      </Col>
                    )}
                  {tax.taxCode && (
                    <Col md={4}>
                      <small className="text-muted fw-bolder">Tax code</small>
                      <div>{tax.taxCode}</div>
                    </Col>
                  )}
                  {((Array.isArray(tax.p45Files) && tax.p45Files.length > 0) ||
                    (typeof tax.p45Files === "string" &&
                      tax.p45Files.trim())) && (
                    <Col md={12}>
                      <small className="text-muted fw-bolder d-block mb-2">
                        P45 documents
                      </small>
                      <div>{linkOrDash(tax.p45Files)}</div>
                    </Col>
                  )}
                  {((Array.isArray(tax.starterChecklistFiles) &&
                    tax.starterChecklistFiles.length > 0) ||
                    (typeof tax.starterChecklistFiles === "string" &&
                      tax.starterChecklistFiles.trim())) && (
                    <Col md={12}>
                      <small className="text-muted fw-bolder d-block mb-2">
                        Starter checklist documents
                      </small>
                      <div>{linkOrDash(tax.starterChecklistFiles)}</div>
                    </Col>
                  )}
                  {tax.pensionAutoEnrolment !== undefined &&
                    tax.pensionAutoEnrolment !== null && (
                      <Col md={4}>
                        <small className="text-muted fw-bolder">
                          Pension auto-enrolment
                        </small>
                        <div>
                          {tax.pensionAutoEnrolment
                            ? "Enrolled in pension scheme"
                            : "Opted out of auto-enrolment"}
                        </div>
                      </Col>
                    )}
                </Row>
              </CardBody>
            </Collapse>
          </Card>

          {/* ===== 5. Bank Details ===== */}
          <Card className="mb-3">
            <CardHeader
              onClick={() => setOpenBank((v) => !v)}
              className="d-flex justify-content-between align-items-center"
              style={{ cursor: "pointer", backgroundColor: "#ededed" }}
            >
              <h5 className="mb-0">Bank Details</h5>
              <i
                className={`mdi mdi-chevron-${
                  openBank ? "up" : "down"
                } bg-dark text-white rounded-circle`}
                style={{
                  width: "20px",
                  height: "20px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "12px",
                }}
              ></i>
            </CardHeader>
            <Collapse isOpen={openBank}>
              <CardBody>
                <Row className="gy-2">
                  {bank.accountHolderName && (
                    <Col md={4}>
                      <small className="text-muted fw-bolder">
                        Account Holder
                      </small>
                      <div>{bank.accountHolderName}</div>
                    </Col>
                  )}
                  {bank.sortCode && (
                    <Col md={4}>
                      <small className="text-muted fw-bolder">Sort Code</small>
                      <div>{bank.sortCode}</div>
                    </Col>
                  )}
                  {bank.accountNumber && (
                    <Col md={4}>
                      <small className="text-muted fw-bolder">
                        Account number
                      </small>
                      <div>{bank.accountNumber}</div>
                    </Col>
                  )}
                  {bank.bankName && (
                    <Col md={4}>
                      <small className="text-muted fw-bolder">Bank Name</small>
                      <div>{bank.bankName}</div>
                    </Col>
                  )}
                </Row>
              </CardBody>
            </Collapse>
          </Card>

          {/* ===== 5b. More About You ===== */}
          {hasMoreAboutYouSection && (
            <Card className="mb-3">
              <CardHeader
                onClick={() => setOpenMoreAboutYou((v) => !v)}
                className="d-flex justify-content-between align-items-center"
                style={{ cursor: "pointer", backgroundColor: "#ededed" }}
              >
                <h5 className="mb-0">More About You</h5>
                <i
                  className={`mdi mdi-chevron-${
                    openMoreAboutYou ? "up" : "down"
                  } bg-dark text-white rounded-circle`}
                  style={{
                    width: "20px",
                    height: "20px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "12px",
                  }}
                ></i>
              </CardHeader>
              <Collapse isOpen={openMoreAboutYou}>
                <CardBody>
                  <Row className="gy-2">
                    {hasText(moreAboutYouHealthText) && (
                      <Col md={12}>
                        <small className="text-muted fw-bolder d-block mb-1">
                          Health &amp; accessibility adjustments
                        </small>
                        <div style={{ whiteSpace: "pre-wrap" }}>
                          {moreAboutYouHealthText}
                        </div>
                      </Col>
                    )}
                    {may.hasUnspentConvictionsOrPendingCases !== undefined &&
                      may.hasUnspentConvictionsOrPendingCases !== null && (
                        <Col md={4}>
                          <small className="text-muted fw-bolder">
                            Unspent convictions or pending cases
                          </small>
                          <div>
                            <Bool
                              value={may.hasUnspentConvictionsOrPendingCases}
                            />
                          </div>
                        </Col>
                      )}
                    {hasText(may.criminalConvictionsDetails) && (
                      <Col md={12}>
                        <small className="text-muted fw-bolder d-block mb-1">
                          Criminal convictions details
                        </small>
                        <div style={{ whiteSpace: "pre-wrap" }}>
                          {may.criminalConvictionsDetails}
                        </div>
                      </Col>
                    )}
                  </Row>
                </CardBody>
              </Collapse>
            </Card>
          )}

          {/* ===== 5c. Work History ===== */}
          {hasWorkHistorySection && (
            <Card className="mb-3">
              <CardHeader
                onClick={() => setOpenWorkHistory((v) => !v)}
                className="d-flex justify-content-between align-items-center"
                style={{ cursor: "pointer", backgroundColor: "#ededed" }}
              >
                <h5 className="mb-0">Work History</h5>
                <i
                  className={`mdi mdi-chevron-${
                    openWorkHistory ? "up" : "down"
                  } bg-dark text-white rounded-circle`}
                  style={{
                    width: "20px",
                    height: "20px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "12px",
                  }}
                ></i>
              </CardHeader>
              <Collapse isOpen={openWorkHistory}>
                <CardBody>
                  <div style={{ whiteSpace: "pre-wrap" }}>
                    {workHistoryText}
                  </div>
                </CardBody>
              </Collapse>
            </Card>
          )}

          <WorkerDetailsCollapsibleCard
            title="Experience"
            isOpen={openAdminExperience}
            onToggle={setOpenAdminExperience}
          >
            {hasText(experienceLevelText) && (
              <Row className="gy-2 mb-3">
                <Col md={4}>
                  <small className="text-muted fw-bolder">
                    Experience level
                  </small>
                  <div>{experienceLevelText}</div>
                </Col>
              </Row>
            )}
            <AdminWorkerProfileFields {...adminFieldProps} />
          </WorkerDetailsCollapsibleCard>

          {/* ===== 6. Preferences ===== */}
          {hasPreferencesSection && (
            <Card className="mb-3">
              <CardHeader
                onClick={() => setOpenWorkPrefs((v) => !v)}
                className="d-flex justify-content-between align-items-center"
                style={{ cursor: "pointer", backgroundColor: "#ededed" }}
              >
                <h5 className="mb-0">Preferences</h5>
                <i
                  className={`mdi mdi-chevron-${
                    openWorkPrefs ? "up" : "down"
                  } bg-dark text-white rounded-circle`}
                  style={{
                    width: "20px",
                    height: "20px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "12px",
                  }}
                ></i>
              </CardHeader>
              <Collapse isOpen={openWorkPrefs}>
                <CardBody>
                  {Array.isArray(sectorsParsedForView) &&
                  sectorsParsedForView.length > 0 ? (
                    <Table size="sm" bordered responsive className="mb-3">
                      <thead>
                        <tr>
                          <th style={{ width: "35%" }}>Sector</th>
                          <th>Roles</th>
                        </tr>
                      </thead>
                      <tbody>
                        {sectorsParsedForView.map((s, idx) => (
                          <tr key={idx}>
                            <td className="text-capitalize">
                              {s.sectorTitle || "-"}
                            </td>
                            <td>
                              {Array.isArray(s.roleTitles) &&
                              s.roleTitles.length
                                ? s.roleTitles.join(", ")
                                : "-"}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  ) : (
                    <div className="text-muted mb-3">-</div>
                  )}
                  <Row className="gy-2">
                    {prefs.preferredWorkRadius !== undefined &&
                      prefs.preferredWorkRadius !== null && (
                        <Col md={4}>
                          <small className="text-muted fw-bolder">
                            Preferred work radius (miles)
                          </small>
                          <div>{prefs.preferredWorkRadius}</div>
                        </Col>
                      )}
                    {Array.isArray(prefs.availability) &&
                      prefs.availability.length > 0 && (
                        <Col md={4}>
                          <small className="text-muted fw-bolder">
                            Availability
                          </small>
                          <div>{humanizeList(prefs.availability)}</div>
                        </Col>
                      )}
                    {Array.isArray(prefs.languages) &&
                      prefs.languages.length > 0 && (
                        <Col md={4}>
                          <small className="text-muted fw-bolder">
                            Languages
                          </small>
                          <div>{humanizeList(prefs.languages)}</div>
                        </Col>
                      )}
                    {Array.isArray(prefs.transport) &&
                      prefs.transport.length > 0 && (
                        <Col md={4}>
                          <small className="text-muted fw-bolder">
                            Transport
                          </small>
                          <div>{humanizeList(prefs.transport)}</div>
                        </Col>
                      )}
                    {prefs.optOut48HourLimit !== undefined &&
                      prefs.optOut48HourLimit !== null && (
                        <Col md={4}>
                          <small className="text-muted fw-bolder">
                            Opted out of 48-hour limit
                          </small>
                          <div>
                            <Bool value={prefs.optOut48HourLimit} />
                          </div>
                        </Col>
                      )}
                    {Array.isArray(prefs.whatMattersMost) &&
                      prefs.whatMattersMost.length > 0 && (
                        <Col md={12}>
                          <small className="text-muted fw-bolder d-block mb-2">
                            What matters most
                          </small>
                          <div className="d-flex flex-wrap gap-2">
                            {prefs.whatMattersMost.map((item, idx) => (
                              <AccentBadge
                                key={idx}
                                variant="info"
                                icon="mdi-heart-outline"
                              >
                                {item}
                              </AccentBadge>
                            ))}
                          </div>
                        </Col>
                      )}
                  </Row>
                </CardBody>
              </Collapse>
            </Card>
          )}

          {hasComplianceDrivingSection && (
            <WorkerDetailsCollapsibleCard
              title="Compliance & Skills"
              isOpen={openCompliance}
              onToggle={setOpenCompliance}
            >
              <Row className="gy-2">
                <Col md={12}>
                  <h6 className="mb-3 fw-bold">Driving licence &amp; DVLA</h6>
                </Col>
                {hasDrivingLicenseFront && (
                  <Col md={4}>
                    <small className="text-muted fw-bolder d-block mb-2">
                      Driving license front
                    </small>
                    <div>{linkOrDash(rtw.drivingLicense)}</div>
                  </Col>
                )}
                {hasDrivingLicenseBack && (
                  <Col md={4}>
                    <small className="text-muted fw-bolder d-block mb-2">
                      Driving license back
                    </small>
                    <div>{linkOrDash(rtw.drivingLicenseBack)}</div>
                  </Col>
                )}
                {rtw.drivingLicenseStatus !== undefined &&
                  rtw.drivingLicenseStatus !== null && (
                    <Col md={4}>
                      <small className="text-muted fw-bolder">
                        Driving licence verified
                      </small>
                      <div>
                        <Bool value={rtw.drivingLicenseStatus} />
                      </div>
                    </Col>
                  )}
                {rtw.drivingLicenseExpiryDate && (
                  <Col md={4}>
                    <small className="text-muted fw-bolder">
                      Driving licence expiry
                    </small>
                    <div>
                      {new Date(
                        rtw.drivingLicenseExpiryDate,
                      ).toLocaleDateString()}
                    </div>
                  </Col>
                )}
                {rtw.dvlaShareCode && String(rtw.dvlaShareCode).trim() && (
                  <Col md={4}>
                    <small className="text-muted fw-bolder">
                      DVLA share code
                    </small>
                    <div>{rtw.dvlaShareCode}</div>
                  </Col>
                )}
                {rtw.licenseNumber && String(rtw.licenseNumber).trim() && (
                  <Col md={4}>
                    <small className="text-muted fw-bolder">
                      Licence number
                    </small>
                    <div>{rtw.licenseNumber}</div>
                  </Col>
                )}
              </Row>
            </WorkerDetailsCollapsibleCard>
          )}

          {(hasDrivingLicenseEvidence || hasDvlaShareCodeValue) && (
            <WorkerDetailsCollapsibleCard
              title="Admin review"
              isOpen={openAdminDriving}
              onToggle={setOpenAdminDriving}
            >
              <AdminWorkerDrivingFields {...adminFieldProps} />
            </WorkerDetailsCollapsibleCard>
          )}

          {hasDbsCertsSection && (
            <Card className="mb-3">
              <CardBody>
                {Array.isArray(comp.dbs) && comp.dbs.length > 0 && (
                  <>
                    <Row className="gy-2 mb-3">
                      <Col md={12}>
                        <h6 className="mb-3 fw-bold">DBS Certificates</h6>
                      </Col>
                    </Row>
                    {comp.dbs.map((dbsItem, idx) => (
                      <Card
                        key={dbsItem.dbsCertId || idx}
                        className="mb-3 border"
                      >
                        <CardBody>
                          <Row className="gy-2">
                            <Col md={12}>
                              <h6 className="mb-2 text-capitalize">
                                {dbsItem.dbsCert?.title || "DBS Certificate"}
                              </h6>
                              <AdminExternalLinks
                                links={[
                                  {
                                    href: UK_DBS_UPDATE_SERVICE_URL,
                                    label: "DBS Update Service checker",
                                  },
                                ]}
                              />
                            </Col>
                            {dbsItem.dbsType && (
                              <Col md={3}>
                                <small className="text-muted fw-bolder">
                                  DBS type
                                </small>
                                <div>
                                  {DBS_TYPE_OPTIONS.find(
                                    (o) => o.value === dbsItem.dbsType,
                                  )?.label || dbsItem.dbsType}
                                </div>
                              </Col>
                            )}
                            {dbsItem.dbsType &&
                              dbsTypeRequiresWorkforce(dbsItem.dbsType) && (
                                <Col md={3}>
                                  <small className="text-muted fw-bolder">
                                    Workforce
                                  </small>
                                  <div>
                                    {dbsItem.workForce === "child"
                                      ? "Child Workforce"
                                      : dbsItem.workForce === "adult"
                                        ? "Adult Workforce"
                                        : "Both"}
                                  </div>
                                </Col>
                              )}
                            {dbsItem.dbsCert?.hasServiceNumber && (
                              <Col md={3}>
                                <small className="text-muted fw-bolder">
                                  Service number
                                </small>
                                <div>
                                  {dbsItem.serviceNumber || "Not provided"}
                                </div>
                              </Col>
                            )}
                            {dbsItem.expiryDate && (
                              <Col md={3}>
                                <small className="text-muted fw-bolder">
                                  Expiry date
                                </small>
                                <div>
                                  {new Date(
                                    dbsItem.expiryDate,
                                  ).toLocaleDateString()}
                                </div>
                              </Col>
                            )}
                            {Array.isArray(dbsItem.dbsFile) &&
                              dbsItem.dbsFile.length > 0 && (
                                <Col md={12}>
                                  <small className="text-muted fw-bolder d-block mb-1">
                                    Files
                                  </small>
                                  <div>
                                    {dbsItem.dbsFile.map(
                                      (fileValue, fileIdx) => (
                                        <div
                                          key={fileIdx}
                                          className={
                                            fileIdx > 0 ? "mt-3" : undefined
                                          }
                                        >
                                          {linkWithText(
                                            fileValue,
                                            `File #${fileIdx + 1}`,
                                          )}
                                        </div>
                                      ),
                                    )}
                                  </div>
                                </Col>
                              )}
                          </Row>
                        </CardBody>
                      </Card>
                    ))}
                  </>
                )}

                {Array.isArray(comp.otherCerts) &&
                  comp.otherCerts.length > 0 && (
                    <>
                      <Row className="gy-2 mb-3">
                        <Col md={12}>
                          <h6 className="mb-3 fw-bold">Other Certificates</h6>
                        </Col>
                      </Row>
                      {comp.otherCerts.map((certItem, idx) => {
                        const certFiles = getOtherCertFiles(certItem);

                        return (
                          <Card
                            key={certItem.CertId || idx}
                            className="mb-3 border"
                          >
                            <CardBody>
                              <Row className="gy-2">
                                <Col md={12}>
                                  <h6 className="mb-2 text-capitalize">
                                    {certItem.Cert?.title || "Certificate"}
                                  </h6>
                                  {isCscsCertificate(
                                    certItem.Cert?.slug,
                                    certItem.Cert?.title,
                                  ) && (
                                    <AdminExternalLinks
                                      links={[
                                        {
                                          href: CSCS_SMART_CHECK_URL,
                                          label: "CSCS Smart Check",
                                        },
                                      ]}
                                    />
                                  )}
                                </Col>
                                <Col md={3}>
                                  <small className="text-muted fw-bolder">
                                    Status
                                  </small>
                                  <div>
                                    <Badge
                                      color={
                                        certItem.status === true
                                          ? "success"
                                          : "danger"
                                      }
                                    >
                                      {certItem.status === true
                                        ? "Verified"
                                        : "Unverified"}
                                    </Badge>
                                  </div>
                                </Col>
                                {certItem.status === true &&
                                  !isCvCertificate(certItem.Cert?.slug) && (
                                    <Col md={3}>
                                      <small className="text-muted fw-bolder">
                                        Expiry Date
                                      </small>
                                      <div>
                                        {certItem.expiryDate
                                          ? new Date(
                                              certItem.expiryDate,
                                            ).toLocaleDateString()
                                          : "N/A"}
                                      </div>
                                    </Col>
                                  )}
                                {certItem.Cert?.hasServiceNumber && (
                                  <Col md={3}>
                                    <small className="text-muted fw-bolder">
                                      Service number
                                    </small>
                                    <div>
                                      {certItem.serviceNumber || "Not provided"}
                                    </div>
                                  </Col>
                                )}
                                {certFiles.length > 0 && (
                                  <Col md={12}>
                                    <small className="text-muted fw-bolder d-block mb-1">
                                      Files
                                    </small>
                                    <div>
                                      {certFiles.map((fileValue, fileIdx) => (
                                        <div
                                          key={fileIdx}
                                          className={
                                            fileIdx > 0 ? "mt-3" : undefined
                                          }
                                        >
                                          {linkWithText(
                                            fileValue,
                                            `File #${fileIdx + 1}`,
                                          )}
                                        </div>
                                      ))}
                                    </div>
                                  </Col>
                                )}
                              </Row>
                            </CardBody>
                          </Card>
                        );
                      })}
                    </>
                  )}
              </CardBody>
            </Card>
          )}

          {hasAdminDbsSection && (
            <WorkerDetailsCollapsibleCard
              title="Admin review"
              isOpen={openAdminDbs}
              onToggle={setOpenAdminDbs}
            >
              <AdminWorkerDbsFields {...adminFieldProps} />
            </WorkerDetailsCollapsibleCard>
          )}

          {hasAdminOtherCertsSection && (
            <WorkerDetailsCollapsibleCard
              title="Admin review"
              isOpen={openAdminOtherCerts}
              onToggle={setOpenAdminOtherCerts}
            >
              <AdminWorkerOtherCertsFields {...adminFieldProps} />
            </WorkerDetailsCollapsibleCard>
          )}

          {/* ===== 8. Declarations & Consents ===== */}
          {Object.keys(decs).some((key) => key !== "infoAccurate") && (
            <Card className="mb-3">
              <CardHeader
                onClick={() => setOpenDeclarations((v) => !v)}
                className="d-flex justify-content-between align-items-center"
                style={{ cursor: "pointer", backgroundColor: "#ededed" }}
              >
                <h5 className="mb-0">Declarations &amp; Consents</h5>
                <i
                  className={`mdi mdi-chevron-${
                    openDeclarations ? "up" : "down"
                  } bg-dark text-white rounded-circle`}
                  style={{
                    width: "20px",
                    height: "20px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "12px",
                  }}
                ></i>
              </CardHeader>
              <Collapse isOpen={openDeclarations}>
                <CardBody>
                  <Row className="gy-2">
                    {decs.contractAndPayrollAgreed !== undefined && (
                      <Col md={4}>
                        <small className="text-muted fw-bolder">
                          Right to work confirmed
                        </small>
                        <div>
                          <Bool value={decs.contractAndPayrollAgreed} />
                        </div>
                      </Col>
                    )}
                    {decs.privacyConsent !== undefined && (
                      <Col md={4}>
                        <small className="text-muted fw-bolder">
                          Privacy policy acknowledged
                        </small>
                        <div>
                          <Bool value={decs.privacyConsent} />
                        </div>
                      </Col>
                    )}
                    {decs.workingTimeAgreement !== undefined && (
                      <Col md={4}>
                        <small className="text-muted fw-bolder">
                          Profile kept up to date
                        </small>
                        <div>
                          <Bool value={decs.workingTimeAgreement} />
                        </div>
                      </Col>
                    )}
                    {decs.termsAndPrivacyAccepted !== undefined && (
                      <Col md={4}>
                        <small className="text-muted fw-bolder">
                          Platform terms of use accepted
                        </small>
                        <div>
                          <Bool value={decs.termsAndPrivacyAccepted} />
                        </div>
                      </Col>
                    )}
                    {decs.hasAcceptedWorkerEngagementAgreement !==
                      undefined && (
                      <Col md={4}>
                        <small className="text-muted fw-bolder">
                          Worker engagement agreement accepted
                        </small>
                        <div>
                          <Bool
                            value={decs.hasAcceptedWorkerEngagementAgreement}
                          />
                        </div>
                      </Col>
                    )}
                    {decs.marketingConsent !== undefined && (
                      <Col md={4}>
                        <small className="text-muted fw-bolder">
                          Marketing consent
                        </small>
                        <div>
                          <Bool value={decs.marketingConsent} />
                        </div>
                      </Col>
                    )}
                    {hasText(decs.declarationProof) && (
                      <Col md={12}>
                        <small className="text-muted fw-bolder d-block mb-2">
                          Signed Worker Platform Agreement
                        </small>
                        <div>
                          {linkWithText(
                            decs.declarationProof,
                            "View signed agreement",
                          )}
                        </div>
                      </Col>
                    )}
                    {hasText(user?.contractSignedAt) && (
                      <Col md={4}>
                        <small className="text-muted fw-bolder">
                          Contract signed at
                        </small>
                        <div>{formatDisplayDate(user.contractSignedAt)}</div>
                      </Col>
                    )}
                    {hasText(user?.agreementLink || worker?.agreementLink) && (
                      <Col md={12}>
                        <small className="text-muted fw-bolder d-block mb-1">
                          Agreement link
                        </small>
                        <a
                          href={user?.agreementLink || worker?.agreementLink}
                          target="_blank"
                          rel="noreferrer"
                          style={linkAnchorStyle}
                        >
                          {user?.agreementLink || worker?.agreementLink}
                        </a>
                      </Col>
                    )}
                  </Row>
                </CardBody>
              </Collapse>
            </Card>
          )}

          <WorkerDetailsCollapsibleCard
            title="Admin review"
            isOpen={openAdminAccount}
            onToggle={setOpenAdminAccount}
          >
            <AdminWorkerAccountFields {...adminFieldProps} />
          </WorkerDetailsCollapsibleCard>

          <WorkerAdminFormFooter
            hint={
              canUpdateWorker
                ? "Save changes after updating verification status and compliance fields."
                : "Review all sections before creating the user. Email and role cannot be changed here."
            }
          >
            {activeFormik.submitCount > 0 &&
              Object.keys(activeFormik.errors).length > 0 && (
                <div className="text-danger small mb-2 text-end">
                  Please fix the validation errors in the form before saving.
                </div>
              )}
            <Button
              color={
                canUpdateWorker
                  ? "primary"
                  : isDecliningWorker
                    ? "danger"
                    : "success"
              }
              type="submit"
              disabled={
                canUpdateWorker
                  ? !!updateWorker?.isPending
                  : createUser.isPending
              }
              className="px-4"
            >
              <i
                className={`mdi ${
                  canUpdateWorker
                    ? "mdi-content-save-outline"
                    : isDecliningWorker
                      ? "mdi-close-circle-outline"
                      : "mdi-check-circle-outline"
                } me-1`}
              />
              {canUpdateWorker
                ? updateWorker?.isPending
                  ? "Updating..."
                  : "Update User"
                : createUser.isPending
                  ? isDecliningWorker
                    ? "Declining..."
                    : "Creating..."
                  : isDecliningWorker
                    ? "Decline user"
                    : "Create User"}
            </Button>
            {!canUpdateWorker && createUser.isError && (
              <div className="text-danger mt-2 text-end small">
                {(() => {
                  const error = createUser.error?.response?.data;
                  if (!error)
                    return "Failed to create user. Please check the form and try again.";

                  if (typeof error.message === "string") {
                    return error.message;
                  }
                  if (error.message?.error) {
                    if (Array.isArray(error.message.error)) {
                      return error.message.error.join(", ");
                    }
                    if (typeof error.message.error === "string") {
                      return error.message.error;
                    }
                  }
                  return "Failed to create user. Please check the form and try again.";
                })()}
              </div>
            )}
          </WorkerAdminFormFooter>
        </Form>
        <ConfirmModal
          isOpen={!!pendingCreateRequest}
          toggle={closeCreateConfirmation}
          title={
            isDecliningWorker
              ? "Confirm decline"
              : "Confirm Right to Work"
          }
          confirmLabel={
            isDecliningWorker
              ? "Confirm and Decline User"
              : "Confirm and Create User"
          }
          cancelLabel="Go Back"
          confirmColor={isDecliningWorker ? "danger" : "success"}
          onConfirm={confirmCreateWorker}
          isLoading={!!createUser.isPending}
        >
          {isDecliningWorker ? (
            <>
              <p className="mb-3">
                You are about to decline this worker&apos;s application. They
                will not be approved for the platform.
              </p>
              <p className="text-muted mb-0">
                Right to Work documents are optional for declines. Confirm only
                if you are sure you want to decline this user.
              </p>
            </>
          ) : (
            <>
              <p className="mb-3">
                Has the worker&apos;s Right to Work been successfully verified
                and recorded?
              </p>
              <p className="text-muted mb-0">
                Only proceed if all required checks have been completed.
              </p>
            </>
          )}
        </ConfirmModal>
      </div>
    </div>
  );
}

export default withRouter(WorkerDetails);
